// 实例详情页：
// - GET /api/instances/{id} 拉详情（在线先内存 dump，离线 fallback DB 快照）
// - useSSESubscription("instance.update", ...) 命中本实例时合并内存快照
// - 30s 慢轮询兜底（DB 关联数据、离线快照、SSE 断线时补偿）
// - 写按钮（can_write=true 才显示）：
//     · restart-game → /api/instances/{id}/restart-game
//   经 ConfirmDialog 二次确认后提交。

import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  Download,
  Gift,
  Play,
  Recycle,
  RefreshCw,
  Search,
  Send,
  Square,
  Store,
  UploadCloud,
} from "lucide-react";
import { apiGet, apiPostForm, APIError } from "@/api/client";
import {
  instanceDetailPath,
  instanceAutoMapHuntObservabilityPath,
  instanceProfitPath,
  instanceRestartGamePath,
  instanceModuleEditPath,
  launchSlotsPath,
  roleLaunchBindingPath,
  roleLaunchBindingsPath,
  roleLaunchBindingRefreshEnvAndStartPath,
  roleLaunchBindingStartPath,
  roleLaunchBindingSyncEnvPath,
} from "@/api/endpoints";
import type {
  AutoMapHuntObservabilityResponse,
  InstanceDetailResponse,
  InstanceModuleConfigEntry,
  InstanceModuleEditResponse,
  InstanceProfitResponse,
  InstanceProfitWindow,
  LaunchSlotEntry,
  LaunchSlotsResponse,
  ProfitCurrencyAmount,
  ProfitEventRecord,
  ProfitMapAggregate,
  ProfitMaterialAmount,
  ProfitSummary,
  RoleLaunchBindingActionResponse,
} from "@/api/types";
import {
  getGameBundle,
  updateGameJSONField,
  type GameBundleResponse,
} from "@/api/games";
import { QueryView } from "@/components/ui/QueryView";
import { StatusBadge } from "@/components/ui/StatusBadge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/Tabs";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Checkbox } from "@/components/ui/Checkbox";
import { Field, Input } from "@/components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { ConfirmDialog } from "@/components/forms/ConfirmDialog";
import { InstanceGameStateCard } from "@/components/instances/InstanceGameStateCard";
import { toast } from "@/components/ui/Sonner";
import { useSession } from "@/hooks/useSession";
import { useSSESubscription } from "@/hooks/useSSESubscription";
import {
  compactNumber,
  formatDurationSec,
  formatMs,
  killsPerMin,
} from "@/lib/format";
import {
  formatModuleRuntimeStatus,
  formatModuleScope,
} from "@/lib/module-status";
import {
  AutoAuctionBidConfigForm,
  AutoSummonConfigForm,
  AutoSuoYaoConfigForm,
  ContinentFleeConfigForm,
  PullMonsterConfigForm,
} from "@/components/modules/ModuleConfigForms";

export const Route = createFileRoute("/instances/$instanceId")({
  component: InstanceDetailRoute,
});

function InstanceDetailRoute() {
  const { instanceId } = useParams({ from: "/instances/$instanceId" });
  return <InstanceDetailView instanceId={instanceId} />;
}

export interface InstanceDetailViewProps {
  instanceId: string;
}

export function InstanceDetailView({ instanceId }: InstanceDetailViewProps) {
  const queryClient = useQueryClient();
  const session = useSession();
  const canWrite = session.data?.can_write ?? false;
  const actor = session.data?.actor ?? "anonymous";

  const q = useQuery({
    queryKey: ["instance", instanceId],
    queryFn: () =>
      apiGet<InstanceDetailResponse>(instanceDetailPath(instanceId)),
    refetchInterval: 30_000,
  });

  useSSESubscription("instance.update", (payload) => {
    const id = typeof payload.instance_id === "string" ? payload.instance_id : "";
    if (id === instanceId) {
      if (payload.reason === "lua_disconnected") {
        void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
      } else {
        queryClient.setQueryData<InstanceDetailResponse>(
          ["instance", instanceId],
          (current) => mergeInstanceUpdate(current, payload),
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["instances"] });
    }
  });

  useSSESubscription("instance.profit_updated", (payload) => {
    const id = typeof payload.instance_id === "string" ? payload.instance_id : "";
    if (id === instanceId) {
      void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
      void queryClient.invalidateQueries({ queryKey: ["instance-profit", instanceId] });
      void queryClient.invalidateQueries({ queryKey: ["instances"] });
    }
  });

  const [restartOpen, setRestartOpen] = useState(false);
  const [showRawJSON, setShowRawJSON] = useState(false);

  async function restartGame() {
    try {
      await apiPostForm<{ ok: boolean; error?: string }>(
        instanceRestartGamePath(instanceId),
        { reason: `webui:${actor}` },
      );
      toast.success("重启游戏已派发");
      void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
    } catch (err) {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("重启游戏失败", { description: msg });
      throw err;
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <h1 className="min-w-0 flex-1 break-all font-mono text-sm font-semibold leading-6">
          实例 {instanceId}
        </h1>
        {canWrite ? (
          <Button
            size="sm"
            variant="destructive"
            className="shrink-0"
            onClick={() => setRestartOpen(true)}
          >
            重启游戏
          </Button>
        ) : null}
      </div>

      <QueryView query={q}>
        {(data) => (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge variant={data.online ? "ok" : "neutral"}>
                {data.online ? "在线" : "离线快照"}
              </StatusBadge>
              {data.account ? (
                <span className="text-xs">
                  归属账号{" "}
                  <Link
                    to="/accounts/$accountId"
                    params={{ accountId: String(data.account.id) }}
                    className="underline"
                  >
                    {data.account.game_account}
                  </Link>
                </span>
              ) : null}
            </div>

            {!data.online ? (
              <OfflineBanner
                offlineSession={data.offline_session}
                launchContext={data.launch_context}
                canWrite={canWrite}
                actor={actor}
                onChanged={() => q.refetch()}
              />
            ) : null}

            <InstanceGameStateCard
              instance={data.instance}
              accountGameId={undefined}
            />

            <InstanceProfitSummaryCard profit={data.profit} />

            <Tabs defaultValue="auto-map">
              <TabsList className="flex h-auto w-full flex-wrap justify-start">
                <TabsTrigger value="auto-map">自动下图</TabsTrigger>
                <TabsTrigger value="flee">回城保护</TabsTrigger>
                <TabsTrigger value="trading-bank">交易行</TabsTrigger>
                <TabsTrigger value="daily">每日</TabsTrigger>
                <TabsTrigger value="modules">其它模块</TabsTrigger>
                <TabsTrigger value="profit">收益</TabsTrigger>
                <TabsTrigger value="map-stats">
                  地图统计
                  {data.map_stats.length > 0 ? ` ${data.map_stats.length}` : ""}
                </TabsTrigger>
                <TabsTrigger value="debug">调试</TabsTrigger>
              </TabsList>

              <TabsContent value="auto-map">
                <InstanceAutoMapHuntCard
                  instanceId={instanceId}
                  data={data}
                  canWrite={canWrite}
                />
              </TabsContent>

              <TabsContent value="flee">
                <InstanceContinentFleeCard
                  instanceId={instanceId}
                  data={data}
                  canWrite={canWrite}
                />
              </TabsContent>

              <TabsContent value="trading-bank">
                <InstanceTradingBankCard
                  instanceId={instanceId}
                  data={data}
                  canWrite={canWrite}
                />
              </TabsContent>

              <TabsContent value="daily">
                <InstanceDailyCard
                  instanceId={instanceId}
                  data={data}
                  canWrite={canWrite}
                />
              </TabsContent>

              <TabsContent value="modules">
                <InstanceOtherModulesCard
                  instanceId={instanceId}
                  data={data}
                  canWrite={canWrite}
                />
              </TabsContent>

              <TabsContent value="profit">
                <InstanceProfitCard instanceId={instanceId} detail={data} />
              </TabsContent>

              <TabsContent value="map-stats">
                <MapStatsCard detail={data} />
              </TabsContent>

              <TabsContent value="debug">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowRawJSON((v) => !v)}
                        className="text-xs underline"
                      >
                        {showRawJSON ? "隐藏" : "显示"}原始 game_state JSON
                      </button>
                      <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
                        （调试用）
                      </span>
                    </CardTitle>
                  </CardHeader>
                  {showRawJSON ? (
                    <CardContent>
                      <pre className="max-h-96 overflow-auto rounded bg-[hsl(var(--muted))] p-2 text-[10px]">
{JSON.stringify(
  data.online ? data.instance : (data.offline_session ?? {}),
  null,
  2,
)}
                      </pre>
                    </CardContent>
                  ) : null}
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </QueryView>

      <ConfirmDialog
        open={restartOpen}
        onOpenChange={setRestartOpen}
        resource="instance"
        action="restart_game"
        target={instanceId}
        actor={actor}
        description="将按当前实例上报的 server_id 设置进程级 target server，并只重启该 Lua 连接对应的 cq2.exe；无法定位 PID 时会拒绝。"
        onConfirm={restartGame}
      />
    </div>
  );
}

function mergeInstanceUpdate(
  current: InstanceDetailResponse | undefined,
  payload: Record<string, unknown>,
): InstanceDetailResponse | undefined {
  if (!current) return current;
  const snapshot =
    payload.summary && typeof payload.summary === "object" && !Array.isArray(payload.summary)
      ? payload.summary
      : payload.instance;
  const nextInstance =
    snapshot && typeof snapshot === "object" && !Array.isArray(snapshot)
      ? { ...current.instance, ...(snapshot as Record<string, unknown>) }
      : current.instance;
  return {
    ...current,
    online: true,
    instance: nextInstance,
  };
}

interface OfflineBindingSlotOption {
  slotID: number;
  slotKey: string;
  agentID: string;
  hostname: string;
  resourceRoot: string;
  gameID: string;
  reason: string;
  state: string;
  expectedGameID: string;
  capacityOK: boolean;
  capacityReason: string;
  cacheMatched: boolean;
  agentMatched: boolean;
}

function offlineBindingStringValue(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return fallback;
}

function offlineBindingNumberValue(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

function offlineBindingBoolValue(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
    if (["0", "false", "no", "off"].includes(normalized)) return false;
  }
  return fallback;
}

function offlineSlotCandidateLabel(reason: string): string {
  switch (reason) {
    case "current_binding":
      return "当前绑定";
    case "offline_session_resource_root":
      return "离线快照目录";
    case "historical_role_binding":
      return "历史角色绑定";
    case "account_game_recent_launch":
      return "账号游戏最近启动";
    case "game_cache":
      return "游戏缓存";
    case "manual_slot":
      return "手动选择";
    default:
      return reason || "候选槽位";
  }
}

function launchSlotMatchesGame(slot: LaunchSlotEntry, gameID: string): boolean {
  if (slot.state !== "enabled") return false;
  return !slot.expected_game_id || !gameID || slot.expected_game_id === gameID;
}

function optionFromLaunchSlot(
  slot: LaunchSlotEntry,
  gameID: string,
  reason = "manual_slot",
): OfflineBindingSlotOption {
  return {
    slotID: slot.id,
    slotKey: slot.slot_key,
    agentID: slot.agent_id,
    hostname: slot.hostname,
    resourceRoot: slot.resource_root,
    gameID: slot.expected_game_id || gameID,
    reason,
    state: slot.state,
    expectedGameID: slot.expected_game_id,
    capacityOK: true,
    capacityReason: "",
    cacheMatched: false,
    agentMatched: false,
  };
}

function buildOfflineBindingSlotOptions(
  candidates: NonNullable<InstanceDetailResponse["launch_context"]>["binding_candidates"],
  slots: LaunchSlotEntry[],
  currentSlotID: number,
  gameID: string,
): OfflineBindingSlotOption[] {
  const byID = new Map<number, OfflineBindingSlotOption>();
  for (const candidate of candidates ?? []) {
    if (!candidate.slot_id) continue;
    byID.set(candidate.slot_id, {
      slotID: candidate.slot_id,
      slotKey: candidate.slot_key ?? "",
      agentID: candidate.agent_id ?? "",
      hostname: candidate.hostname ?? "",
      resourceRoot: candidate.resource_root ?? "",
      gameID: candidate.game_id ?? gameID,
      reason: candidate.reason ?? "",
      state: "enabled",
      expectedGameID: candidate.game_id ?? "",
      capacityOK: candidate.capacity_ok,
      capacityReason: candidate.capacity_reason ?? "",
      cacheMatched: candidate.cache_matched ?? false,
      agentMatched: candidate.agent_matched ?? false,
    });
  }
  for (const slot of slots) {
    if (!launchSlotMatchesGame(slot, gameID) && slot.id !== currentSlotID) {
      continue;
    }
    const existing = byID.get(slot.id);
    if (existing) {
      byID.set(slot.id, {
        ...existing,
        slotKey: existing.slotKey || slot.slot_key,
        agentID: existing.agentID || slot.agent_id,
        hostname: existing.hostname || slot.hostname,
        resourceRoot: existing.resourceRoot || slot.resource_root,
        state: slot.state,
        expectedGameID: slot.expected_game_id,
      });
    } else {
      byID.set(slot.id, optionFromLaunchSlot(slot, gameID, slot.id === currentSlotID ? "current_binding" : "manual_slot"));
    }
  }
  return Array.from(byID.values()).sort((left, right) => {
    if (left.slotID === currentSlotID) return -1;
    if (right.slotID === currentSlotID) return 1;
    if (left.capacityOK !== right.capacityOK) return left.capacityOK ? -1 : 1;
    if (left.agentID !== right.agentID) return left.agentID.localeCompare(right.agentID);
    return left.slotID - right.slotID;
  });
}

function offlineBindingSlotOptionLabel(option: OfflineBindingSlotOption, currentSlotID: number): string {
  const parts = [
    `#${option.slotID}`,
    option.slotKey || option.resourceRoot || "slot",
    offlineSlotCandidateLabel(option.slotID === currentSlotID ? "current_binding" : option.reason),
  ];
  if (option.state && option.state !== "enabled") parts.push(option.state);
  if (!option.capacityOK) parts.push("容量不足");
  return parts.join(" · ");
}

function offlineBindingSlotOptionDescription(option: OfflineBindingSlotOption): string {
  const markers = [
    option.hostname || option.agentID,
    option.cacheMatched ? "cache matched" : "",
    option.agentMatched ? "agent matched" : "",
    option.expectedGameID ? `expected ${option.expectedGameID}` : "",
    option.capacityOK ? "" : option.capacityReason || "容量不足",
  ].filter(Boolean);
  return [option.resourceRoot || "未上报目录", ...markers].join(" · ");
}

interface OfflineBannerProps {
  offlineSession: InstanceDetailResponse["offline_session"];
  launchContext: InstanceDetailResponse["launch_context"];
  canWrite: boolean;
  actor: string;
  onChanged: () => void;
}

function OfflineBanner({
  offlineSession,
  launchContext,
  canWrite,
  actor,
  onChanged,
}: OfflineBannerProps) {
  const [pendingMode, setPendingMode] = useState<"start" | "refresh-start" | "sync" | null>(null);
  const bindingID = Number(launchContext?.binding?.id ?? 0);
  const slotID = Number(launchContext?.slot?.id ?? launchContext?.binding?.slot_id ?? 0);
  const gameID = String(
    launchContext?.binding?.game_id ?? offlineSession?.game_id ?? "",
  );
  const candidates = launchContext?.binding_candidates ?? [];
  const slotsQuery = useQuery({
    queryKey: ["launch-slots", "offline-instance-binding", "all"],
    queryFn: () => apiGet<LaunchSlotsResponse>(launchSlotsPath()),
    enabled: Boolean(offlineSession && launchContext),
    staleTime: 15_000,
  });
  const slotOptions = useMemo(
    () => buildOfflineBindingSlotOptions(candidates, slotsQuery.data?.items ?? [], slotID, gameID),
    [candidates, slotsQuery.data?.items, slotID, gameID],
  );
  const [selectedSlotID, setSelectedSlotID] = useState("");
  useEffect(() => {
    if (slotOptions.length === 0) {
      setSelectedSlotID("");
      return;
    }
    setSelectedSlotID((current) => {
      if (current && slotOptions.some((option) => String(option.slotID) === current)) {
        return current;
      }
      if (slotID && slotOptions.some((option) => option.slotID === slotID)) {
        return String(slotID);
      }
      return String(slotOptions[0]?.slotID ?? "");
    });
  }, [slotID, slotOptions]);
  const startMutation = useMutation({
    mutationFn: async (mode: "start" | "refresh-start" | "sync") => {
      if (!bindingID) throw new Error("缺少绑定槽位");
      const path =
        mode === "start"
          ? roleLaunchBindingStartPath(bindingID)
          : mode === "refresh-start"
            ? roleLaunchBindingRefreshEnvAndStartPath(bindingID)
            : roleLaunchBindingSyncEnvPath(bindingID);
      return apiPostForm(path, {});
    },
    onSuccess: (_, mode) => {
      toast.success(
        mode === "sync"
          ? "已同步 env 到固定槽位"
          : mode === "refresh-start"
            ? "已刷新 env 并提交固定槽位启动"
            : "已提交固定槽位启动",
      );
      onChanged();
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "固定槽位操作失败");
    },
  });
  const saveBindingMutation = useMutation({
    mutationFn: async () => {
      if (!offlineSession) throw new Error("缺少离线角色快照");
      const nextSlotID = Number(selectedSlotID);
      if (!nextSlotID) throw new Error("请选择固定槽位");
      if (!offlineSession.account_id || !offlineSession.game_id || !offlineSession.server_id) {
        throw new Error("离线快照缺少账号、游戏或区服信息");
      }
      const binding = launchContext?.binding ?? {};
      const roleName = bindingID
        ? offlineBindingStringValue(binding.role_name, offlineSession.role_name)
        : offlineBindingStringValue(offlineSession.role_name);
      const expectedRoleName = bindingID
        ? offlineBindingStringValue(binding.expected_role_name, offlineSession.role_name)
        : offlineBindingStringValue(offlineSession.role_name);
      const fields = {
        account_id: String(offlineSession.account_id),
        game_id: offlineBindingStringValue(binding.game_id, offlineSession.game_id),
        server_id: offlineBindingStringValue(binding.server_id, offlineSession.server_id),
        role_id: bindingID ? offlineBindingStringValue(binding.role_id) : "",
        role_name: roleName,
        expected_role_name: expectedRoleName,
        slot_id: String(nextSlotID),
        slot_ordinal: String(offlineBindingNumberValue(binding.slot_ordinal, 0)),
        status: bindingID
          ? offlineBindingStringValue(binding.status, roleName ? "bound" : "pending")
          : "pending",
        desired_start_enabled: offlineBindingBoolValue(binding.desired_start_enabled, true)
          ? "1"
          : "0",
      };
      const path = bindingID ? roleLaunchBindingPath(bindingID) : roleLaunchBindingsPath();
      return apiPostForm<RoleLaunchBindingActionResponse>(path, fields);
    },
    onSuccess: () => {
      toast.success(bindingID ? "已更新固定槽位绑定" : "已保存固定槽位绑定");
      onChanged();
    },
    onError: (error) => {
      toast.error(bindingID ? "更新固定槽位绑定失败" : "保存固定槽位绑定失败", {
        description: error instanceof Error ? error.message : String(error),
      });
    },
  });
  const canStart = canWrite && Boolean(launchContext?.can_start);
  const latestJob = launchContext?.latest_launch_job ?? null;
  const canSync = canWrite && Boolean(bindingID) && Boolean(launchContext?.env_snapshot);
  const blockers = launchContext?.blockers ?? [];
  const selectedSlot = slotOptions.find((option) => String(option.slotID) === selectedSlotID);
  const bindingSlotChanged =
    Boolean(bindingID) && Boolean(selectedSlotID) && Number(selectedSlotID) !== slotID;
  const canSaveBinding =
    canWrite &&
    Boolean(selectedSlotID) &&
    (!bindingID || bindingSlotChanged) &&
    !saveBindingMutation.isPending;
  const actionLabel =
    pendingMode === "sync"
      ? "sync_env"
      : pendingMode === "refresh-start"
        ? "refresh_env_and_start"
        : "start";
  const actionDescription =
    pendingMode === "sync"
      ? "只会写入目标槽位 env.json，不启动游戏。"
      : pendingMode === "refresh-start"
        ? "会先通过账号 CDP 刷新 env 快照，再写入绑定槽位目录并启动游戏。"
        : "会使用账号 env 快照写入绑定槽位目录，然后启动游戏。";
  return (
    <div className="rounded border border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/10 p-3 text-xs">
      <div className="font-medium">⚠ 该实例已离线</div>
      <p className="mt-1 text-[hsl(var(--muted-foreground))]">
        显示的是最后一次落库的快照
        {offlineSession?.last_seen_at ? (
          <>
            （最后通讯{" "}
            <span className="font-mono">
              {formatMs(offlineSession.last_seen_at)}
            </span>
          </>
        ) : null}
        {offlineSession?.connected_at ? (
          <>
            ，上次连接{" "}
            <span className="font-mono">
              {formatMs(offlineSession.connected_at)}
            </span>
          </>
        ) : null}
        {offlineSession ? "）" : null}
        。此页只能通过绑定的固定槽位重新启动，不能直接沿用离线快照里的旧目录。
      </p>
      {launchContext ? (
        <div className="mt-3 rounded border bg-[hsl(var(--background))]/70 p-2">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge variant={launchContext.can_start ? "ok" : "warn"}>
              {launchContext.can_start ? "可固定槽位启动" : "启动条件未满足"}
            </StatusBadge>
            {slotID ? (
              <span className="font-mono">
                slot #{slotID} {launchContext.target_slot_resource_root}
              </span>
            ) : null}
          </div>
          <div className="mt-2 grid gap-1 text-[11px] text-[hsl(var(--muted-foreground))] md:grid-cols-2">
            <div>
              env 来源：{" "}
              <span className="font-mono">
                {launchContext.env_source_resource_root || "未刷新"}
              </span>
            </div>
            <div>
              env 快照：{" "}
              <span className="font-mono">
                {String(launchContext.env_snapshot?.env_sha256 ?? "").slice(0, 8) ||
                  "缺失"}
              </span>
            </div>
            {latestJob ? (
              <>
                <div>
                  最近 job：{" "}
                  <span className="font-mono">
                    #{String(latestJob.id ?? "")} {String(latestJob.status ?? "")}
                  </span>
                </div>
                {latestJob.error ? (
                  <div className="text-[hsl(var(--destructive))]">
                    {String(latestJob.error)}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
          {blockers.length > 0 ? (
            <ul className="mt-2 space-y-1 text-[hsl(var(--muted-foreground))]">
              {blockers.map((blocker) => (
                <li key={blocker.code}>- {blocker.message}</li>
              ))}
            </ul>
          ) : null}
          {slotOptions.length > 0 ? (
            <div className="mt-3 grid gap-2 rounded border bg-[hsl(var(--muted))]/30 p-2 md:grid-cols-[minmax(0,1fr)_auto]">
              <select
                className="h-8 min-w-0 rounded border bg-[hsl(var(--background))] px-2 font-mono text-[11px]"
                aria-label={bindingID ? "改绑固定槽位" : "选择固定槽位"}
                value={selectedSlotID}
                onChange={(event) => setSelectedSlotID(event.target.value)}
                disabled={!canWrite || saveBindingMutation.isPending}
              >
                {slotOptions.map((option) => (
                  <option key={option.slotID} value={String(option.slotID)}>
                    {offlineBindingSlotOptionLabel(option, slotID)}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                variant="outline"
                onClick={() => saveBindingMutation.mutate()}
                disabled={!canSaveBinding}
              >
                {bindingID ? "更新绑定" : "保存绑定"}
              </Button>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] md:col-span-2">
                {selectedSlot
                  ? offlineBindingSlotOptionDescription(selectedSlot)
                  : "请选择固定槽位"}
              </div>
            </div>
          ) : slotsQuery.isFetching ? (
            <div className="mt-3 rounded border bg-[hsl(var(--muted))]/30 p-2 text-[11px] text-[hsl(var(--muted-foreground))]">
              正在加载可用固定槽位...
            </div>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => setPendingMode("start")}
              disabled={!canStart || startMutation.isPending}
            >
              <Play className="mr-1 h-3.5 w-3.5" />
              启动游戏
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPendingMode("refresh-start")}
              disabled={!canWrite || !bindingID || startMutation.isPending}
            >
              <RefreshCw className="mr-1 h-3.5 w-3.5" />
              刷新 env 并启动
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setPendingMode("sync")}
              disabled={!canSync || startMutation.isPending}
            >
              <UploadCloud className="mr-1 h-3.5 w-3.5" />
              仅同步 env
            </Button>
          </div>
        </div>
      ) : null}
      <ConfirmDialog
        open={pendingMode !== null}
        onOpenChange={(open) => {
          if (!open) setPendingMode(null);
        }}
        resource="role_launch_binding"
        action={actionLabel}
        target={String(bindingID || "")}
        actor={actor}
        description={actionDescription}
        confirmVariant={pendingMode === "sync" ? "default" : "destructive"}
        onConfirm={async () => {
          if (!pendingMode) return;
          await startMutation.mutateAsync(pendingMode);
          setPendingMode(null);
        }}
      />
    </div>
  );
}

interface InstanceAutoMapHuntCardProps {
  instanceId: string;
  data: InstanceDetailResponse;
  canWrite: boolean;
}

function InstanceAutoMapHuntCard({
  instanceId,
  data,
  canWrite,
}: InstanceAutoMapHuntCardProps) {
  const queryClient = useQueryClient();
  const gameID = String(data.instance.game_id || data.offline_session?.game_id || "fgcq");
  const config = data.module_configs?.auto_map_hunt;
  const bossChallengeConfig = data.module_configs?.boss_challenge;
  const parsed = useMemo(
    () => parseAutoMapHuntDesired(config?.desired_config_json || "{}"),
    [config?.desired_config_json],
  );
  const parsedBossChallenge = useMemo(
    () => parseBossChallengeDesired(bossChallengeConfig?.desired_config_json || "{}"),
    [bossChallengeConfig?.desired_config_json],
  );
  const [enabled, setEnabled] = useState(parsed.enabled);
  const [cutUpgradeEnabled, setCutUpgradeEnabled] = useState(parsed.cutUpgradeEnabled);
  const [bossChallengeEnabled, setBossChallengeEnabled] = useState(
    parsedBossChallenge.enabled,
  );
  const [selectAllMaps, setSelectAllMaps] = useState(parsed.selectAllMaps);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(parsed.selectedMapIds),
  );
  const [activeContinentID, setActiveContinentID] = useState("");
  const runtime = parseRuntimeJSON(config?.runtime_status_json);
  const patrolRuntime = parseAutoMapHuntPatrolRuntime(runtime.patrol);

  const observabilityQ = useQuery({
    queryKey: ["instance-auto-map-hunt-observability", instanceId],
    queryFn: async () =>
      (await apiGet<AutoMapHuntObservabilityResponse | undefined>(
        instanceAutoMapHuntObservabilityPath(instanceId),
      )) ?? emptyAutoMapHuntObservability(instanceId),
    enabled: Boolean(instanceId),
    refetchInterval: false,
  });

  useEffect(() => {
    setEnabled(parsed.enabled);
    setCutUpgradeEnabled(parsed.cutUpgradeEnabled);
    setSelectAllMaps(parsed.selectAllMaps);
    setSelected(new Set(parsed.selectedMapIds));
  }, [parsed]);

  useEffect(() => {
    setBossChallengeEnabled(parsedBossChallenge.enabled);
  }, [parsedBossChallenge]);

  const bundleQ = useQuery({
    queryKey: ["game-bundle", gameID],
    queryFn: () => getGameBundle(gameID),
    enabled: Boolean(gameID),
  });

  const continentGroups = useMemo(() => {
    const bundle = bundleQ.data;
    if (!bundle) return [];
    return bundle.bundle.continents
      .map((continent) => ({
        continent,
        maps: bundle.bundle.maps.filter(
          (m) => m.continent_id === continent.continent_id,
        ),
      }))
      .filter((group) => group.maps.length > 0);
  }, [bundleQ.data]);

  useEffect(() => {
    if (continentGroups.length === 0) {
      if (activeContinentID) setActiveContinentID("");
      return;
    }
    if (
      !activeContinentID ||
      !continentGroups.some(
        (group) => group.continent.continent_id === activeContinentID,
      )
    ) {
      setActiveContinentID(continentGroups[0].continent.continent_id);
    }
  }, [activeContinentID, continentGroups]);

  const mut = useMutation({
    mutationFn: async ({
      desired,
      bossDesired,
      action,
      syncBoss = true,
    }: {
      desired: string;
      bossDesired: string;
      action: "save" | "save_and_apply" | "clear" | "clear_and_apply";
      syncBoss?: boolean;
    }) => {
      if (syncBoss && (action === "save" || action === "save_and_apply")) {
        await apiPostForm<InstanceModuleEditResponse>(
          instanceModuleEditPath(instanceId, "boss_challenge"),
          {
            desired_config_json: bossDesired,
            action,
          },
        );
      }
      const response = await apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, "auto_map_hunt"),
        {
          desired_config_json: desired,
          action,
        },
      );
      return response;
    },
    onSuccess: () => {
      toast.success("自动下图实例配置已保存");
      void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
    },
    onError: (err) => {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("保存自动下图配置失败", { description: msg });
    },
  });
  const generatePatrolMut = useMutation({
    mutationFn: () =>
      apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, "auto_map_hunt"),
        {
          action: "generate_patrol_template",
          payload_json: JSON.stringify({ visionRange: 13 }),
        },
      ),
    onSuccess: async (response) => {
      const draft = parsePatrolTemplateDraftFromRuntime(response.config.runtime_status_json);
      if (!draft) {
        toast.error("生成失败：未返回模板草稿");
        return;
      }
      const bundle = await getGameBundle(gameID);
      const nextTemplates = appendPatrolTemplateDraft(
        bundle.bundle.patrolTemplates,
        draft,
      );
      await updateGameJSONField(
        gameID,
        "patrolTemplates",
        nextTemplates,
        bundle.bundle.version,
      );
      toast.success("观察点模板草稿已追加到游戏配置");
      void queryClient.invalidateQueries({ queryKey: ["game-bundle", gameID] });
      void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
    },
    onError: (err) => {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("生成观察点模板失败", { description: msg });
    },
  });

  function buildDesired(bundle: GameBundleResponse) {
    const ordered = bundle.bundle.maps
      .map((m) => m.map_id)
      .filter((id) => selected.has(id));
    return JSON.stringify(
      selectAllMaps
        ? {
            enabled,
            cutUpgrade: {
              enabled: cutUpgradeEnabled,
              mode: "privilege_map",
              stopAtMultiplier: 8,
            },
            selectAllMaps: true,
          }
        : {
            enabled,
            cutUpgrade: {
              enabled: cutUpgradeEnabled,
              mode: "privilege_map",
              stopAtMultiplier: 8,
            },
            selectAllMaps: false,
            selectedMapIds: ordered,
          },
      null,
      2,
    );
  }

  function buildBossChallengeDesired() {
    return JSON.stringify({ enabled: bossChallengeEnabled }, null, 2);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>自动下图地图配置</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 text-xs">
          {config?.layers?.length ? (
            config.layers.map((layer) => (
              <StatusBadge
                key={`${layer.scope}:${layer.scope_key}:${layer.module}`}
                variant={layer.scope === "instance" ? "ok" : "neutral"}
              >
                {formatModuleScope(layer.scope)}
              </StatusBadge>
            ))
          ) : (
            <StatusBadge variant="neutral">无覆盖</StatusBadge>
          )}
          {config?.instance_override ? (
            <span className="text-[hsl(var(--muted-foreground))]">
              当前实例已覆盖账号默认
            </span>
          ) : (
            <span className="text-[hsl(var(--muted-foreground))]">
              当前使用机器/游戏/账号层合并结果
            </span>
          )}
        </div>

        <AutoMapHuntObservabilityPanel query={observabilityQ} />

        <AutoMapHuntPatrolRuntimePanel patrol={patrolRuntime} />

        <QueryView query={bundleQ}>
          {(bundle) => (
            <div className="space-y-4">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={enabled}
                  disabled={!canWrite}
                  onCheckedChange={(v) => setEnabled(v === true)}
                />
                启用自动挂机
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={cutUpgradeEnabled}
                  disabled={!canWrite || mut.isPending}
                  onCheckedChange={(v) => setCutUpgradeEnabled(v === true)}
                />
                升级切割
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={selectAllMaps}
                  disabled={!canWrite || mut.isPending}
                  onCheckedChange={(v) => setSelectAllMaps(v === true)}
                />
                动态全选地图
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={bossChallengeEnabled}
                  disabled={!canWrite || mut.isPending}
                  onCheckedChange={(v) => setBossChallengeEnabled(v === true)}
                />
                自动挑战必争BOSS
              </label>
              {canWrite ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={mut.isPending || selectAllMaps}
                    onClick={() =>
                      setSelected(new Set(selectableAutoMapIDs(bundle)))
                    }
                  >
                    全选可选地图
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={mut.isPending || selectAllMaps}
                    onClick={() =>
                      setSelected((prev) => {
                        const all = bundle.bundle.maps.map((m) => m.map_id);
                        const selectable = selectableAutoMapIDs(bundle);
                        return invertSelectedMapIDs(prev, all, selectable);
                      })
                    }
                  >
                    反选可选地图
                  </Button>
                </div>
              ) : null}
              {continentGroups.length > 0 ? (
                <Tabs
                  value={activeContinentID}
                  onValueChange={setActiveContinentID}
                  className="space-y-3"
                >
                  <TabsList className="flex h-auto w-full flex-wrap justify-start">
                    {continentGroups.map(({ continent, maps }) => {
                      const selectedCount = maps.filter((m) =>
                        selected.has(m.map_id),
                      ).length;
                      return (
                        <TabsTrigger
                          key={continent.continent_id}
                          value={continent.continent_id}
                          aria-label={continent.name}
                          className="gap-2"
                        >
                          <span>{continent.name}</span>
                          <span className="font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                            {selectedCount}/{maps.length}
                          </span>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>

                  {continentGroups.map(({ continent, maps }) => {
                    const continentMapIDs = maps.map((m) => m.map_id);
                    const continentSelectableIDs = selectableAutoMapIDs(
                      bundle,
                      continent.continent_id,
                    );
                    const allSelectableSelected =
                      continentSelectableIDs.length > 0 &&
                      continentSelectableIDs.every((id) => selected.has(id));
                    const hasSelectedInContinent = continentMapIDs.some((id) =>
                      selected.has(id),
                    );
                    const continentChecked = allSelectableSelected
                      ? true
                      : hasSelectedInContinent
                        ? "indeterminate"
                        : false;
                    const canToggleContinent =
                      continentSelectableIDs.length > 0 || hasSelectedInContinent;
                    return (
                      <TabsContent
                        key={continent.continent_id}
                        value={continent.continent_id}
                        className="mt-0 space-y-3"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <label className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={continentChecked}
                              disabled={
                                !canWrite || mut.isPending || selectAllMaps || !canToggleContinent
                              }
                              aria-label={`切换${continent.name}地图选择`}
                              onCheckedChange={() =>
                                setSelected((prev) => {
                                  if (allSelectableSelected) {
                                    return removeSelectedMapIDs(prev, continentMapIDs);
                                  }
                                  if (continentSelectableIDs.length === 0) {
                                    return removeSelectedMapIDs(prev, continentMapIDs);
                                  }
                                  return addSelectedMapIDs(
                                    prev,
                                    continentSelectableIDs,
                                  );
                                })
                              }
                            />
                            <span>当前大陆地图</span>
                            {!continent.enabled ? (
                              <StatusBadge variant="neutral">停用</StatusBadge>
                            ) : null}
                          </label>
                          <span className="text-xs text-[hsl(var(--muted-foreground))]">
                            已选 {maps.filter((m) => selected.has(m.map_id)).length} /{" "}
                            {maps.length}
                          </span>
                        </div>
                        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                          {maps.map((m) => {
                            const blocked = !continent.enabled || m.unreachable;
                            const checked = selected.has(m.map_id);
                            const disabled = !canWrite || selectAllMaps || (blocked && !checked);
                            return (
                              <label
                                key={m.map_id}
                                className="flex items-start gap-2 rounded border border-[hsl(var(--border))] p-2 text-xs"
                              >
                                <Checkbox
                                  checked={checked}
                                  disabled={disabled}
                                  onCheckedChange={(v) => {
                                    setSelected((prev) => {
                                      const next = new Set(prev);
                                      if (v === true) next.add(m.map_id);
                                      else next.delete(m.map_id);
                                      return next;
                                    });
                                  }}
                                />
                                <span>
                                  <span className="block font-mono">{m.map_id}</span>
                                  <span>{m.name}</span>
                                  {disabled && canWrite ? (
                                    <span className="block text-[hsl(var(--muted-foreground))]">
                                      {m.unreachable_reason || "continent_disabled"}
                                    </span>
                                  ) : null}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </TabsContent>
                    );
                  })}
                </Tabs>
              ) : (
                <div className="rounded border border-[hsl(var(--border))] p-3 text-xs text-[hsl(var(--muted-foreground))]">
                  当前游戏配置里还没有可展示的大陆地图。
                </div>
              )}
              {canWrite ? (
                <div className="flex flex-wrap justify-end gap-2">
                  {config?.instance_override ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={mut.isPending}
                      onClick={() =>
                        mut.mutate({
                          desired: buildDesired(bundle),
                          bossDesired: buildBossChallengeDesired(),
                          action: data.online ? "clear_and_apply" : "clear",
                          syncBoss: false,
                        })
                      }
                    >
                      清除实例覆盖
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={mut.isPending}
                    onClick={() =>
                      mut.mutate({
                        desired: buildDesired(bundle),
                        bossDesired: buildBossChallengeDesired(),
                        action: "save",
                      })
                    }
                  >
                    保存实例覆盖
                  </Button>
                  <Button
                    size="sm"
                    disabled={mut.isPending || !data.online}
                    onClick={() =>
                      mut.mutate({
                        desired: buildDesired(bundle),
                        bossDesired: buildBossChallengeDesired(),
                        action: "save_and_apply",
                      })
                    }
                  >
                    保存并下发当前实例
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={generatePatrolMut.isPending || !data.online}
                    onClick={() => generatePatrolMut.mutate()}
                  >
                    {generatePatrolMut.isPending ? "生成中…" : "生成观察点模板"}
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </QueryView>
      </CardContent>
    </Card>
  );
}

function AutoMapHuntObservabilityPanel({
  query,
}: {
  query: UseQueryResult<AutoMapHuntObservabilityResponse>;
}) {
  const data = query.data;
  const trace = normalizeAutoMapHuntTrace(data?.trace);
  const selected = trace?.selected ?? null;
  const notSelected = Array.isArray(data?.not_selected) ? data.not_selected : [];
  const coordination = data?.coordination;
  const counts = coordination?.counts;
  const preferred = Array.isArray(coordination?.preferred)
    ? coordination.preferred
    : [];
  const reservations = Array.isArray(coordination?.reservations)
    ? coordination.reservations
    : [];
  const leases = Array.isArray(coordination?.leases) ? coordination.leases : [];
  const signals = Array.isArray(coordination?.signals) ? coordination.signals : [];
  const presence = Array.isArray(coordination?.presence) ? coordination.presence : [];
  const totalCoordination =
    (counts?.leases ?? leases.length) +
    (counts?.signals ?? signals.length) +
    (counts?.presence ?? presence.length) +
    (counts?.reservations ?? reservations.length) +
    (counts?.preferred ?? preferred.length);

  return (
    <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 p-3 text-xs">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge variant={selected ? "ok" : trace ? "warn" : "neutral"}>
            选图观测
          </StatusBadge>
          {data?.canonical_server_id ? (
            <span className="font-mono text-[hsl(var(--muted-foreground))]">
              canonical={data.canonical_server_id}
            </span>
          ) : null}
          {query.isFetching ? (
            <span className="text-[hsl(var(--muted-foreground))]">刷新中</span>
          ) : null}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          <RefreshCw className="mr-1 h-3.5 w-3.5" />
          刷新观测
        </Button>
      </div>

      {query.isError ? (
        <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-2 text-[hsl(var(--danger))]">
          观测读取失败：{query.error instanceof Error ? query.error.message : String(query.error)}
        </div>
      ) : null}

      <div className="grid gap-3 lg:grid-cols-3">
        <div className="space-y-2">
          <div className="font-medium">最近选择</div>
          {selected ? (
            <div className="space-y-1 rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{selected.map_name || selected.map_id}</span>
                {selected.map_id ? (
                  <span className="font-mono text-[hsl(var(--muted-foreground))]">
                    {selected.map_id}
                  </span>
                ) : null}
              </div>
              <div className="grid gap-1 sm:grid-cols-2">
                <Metric
                  label="总分"
                  value={formatScore(selected.score)}
                  mono
                />
                <Metric
                  label="优先级"
                  value={String(selected.priority_tier ?? 0)}
                  mono
                />
                <Metric
                  label="窗口"
                  value={selected.window_kind || "—"}
                  mono={Boolean(selected.window_kind)}
                />
                <Metric
                  label="ETA"
                  value={formatArrivalETA(selected.arrival_eta_ms ?? selected.arrival_eta)}
                  mono
                />
              </div>
              {selected.coordination_reason ? (
                <div className="text-[hsl(var(--muted-foreground))]">
                  coordination：{selected.coordination_reason}
                </div>
              ) : null}
              {trace?.top_reject_reason ? (
                <div className="text-[hsl(var(--warn))]">
                  top reject：{trace.top_reject_reason}
                </div>
              ) : null}
            </div>
          ) : trace ? (
            <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-2">
              <div className="font-medium">{trace.event || "no_target"}</div>
              <div className="text-[hsl(var(--muted-foreground))]">
                {trace.top_reject_reason || trace.reason || "本轮没有选中目标。"}
              </div>
            </div>
          ) : (
            <div className="rounded border border-dashed border-[hsl(var(--border))] p-2 text-[hsl(var(--muted-foreground))]">
              暂无 selection trace；模块未上报或低优先级 trace 已过期。
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="font-medium">未选原因</div>
          {notSelected.length > 0 ? (
            <div className="space-y-1">
              {notSelected.slice(0, 5).map((item) => (
                <div
                  key={`${item.rank}:${item.map_id}:${item.target_key}:${item.window_kind}`}
                  className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-2"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono">#{item.rank}</span>
                    <span className="font-medium">{item.map_id || "—"}</span>
                    <span className="font-mono text-[hsl(var(--muted-foreground))]">
                      {item.window_kind || "window=—"}
                    </span>
                  </div>
                  <div className="mt-1 text-[hsl(var(--muted-foreground))]">
                    {item.local_reject_reason ||
                      item.coordination_reason ||
                      (item.local_ready ? "可选但未成为本轮首选" : "无拒绝原因")}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded border border-dashed border-[hsl(var(--border))] p-2 text-[hsl(var(--muted-foreground))]">
              暂无 candidate summary；页面保持可用。
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="font-medium">协调状态</div>
          <div className="grid grid-cols-2 gap-2">
            <Metric label="peer lease" value={String(counts?.leases ?? leases.length)} mono />
            <Metric label="signals" value={String(counts?.signals ?? signals.length)} mono />
            <Metric label="presence" value={String(counts?.presence ?? presence.length)} mono />
            <Metric
              label="preferred"
              value={String(counts?.preferred ?? preferred.length)}
              mono
            />
          </div>
          {preferred[0] ? (
            <CoordinationItem
              label="本实例 reservation"
              mapID={preferred[0].map_id}
              reason={preferred[0].reason}
              score={preferred[0].reservation_score}
              expiresAt={preferred[0].expires_at}
            />
          ) : reservations[0] ? (
            <CoordinationItem
              label="peer reservation"
              mapID={reservations[0].map_id}
              reason={reservations[0].role_name || reservations[0].instance_id}
              score={reservations[0].reservation_score}
              expiresAt={reservations[0].expires_at}
            />
          ) : leases[0] ? (
            <CoordinationItem
              label="peer lease"
              mapID={leases[0].map_id}
              reason={leases[0].role_name || leases[0].instance_id}
              expiresAt={leases[0].expires_at}
            />
          ) : totalCoordination > 0 ? (
            <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-2 text-[hsl(var(--muted-foreground))]">
              当前只有 presence/signal 提示，无占用或 reservation。
            </div>
          ) : (
            <div className="rounded border border-dashed border-[hsl(var(--border))] p-2 text-[hsl(var(--muted-foreground))]">
              同区暂无活跃协调提示。
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CoordinationItem({
  label,
  mapID,
  reason,
  score,
  expiresAt,
}: {
  label: string;
  mapID?: string;
  reason?: string;
  score?: number;
  expiresAt?: number;
}) {
  return (
    <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-2">
      <div className="font-medium">{label}</div>
      <div className="mt-1 flex flex-wrap gap-2 text-[hsl(var(--muted-foreground))]">
        <span className="font-mono">{mapID || "map=—"}</span>
        {reason ? <span>{reason}</span> : null}
        {score !== undefined ? <span className="font-mono">score={formatScore(score)}</span> : null}
      </div>
      {expiresAt ? (
        <div className="mt-1 text-[hsl(var(--muted-foreground))]">
          过期：{formatWallTime(expiresAt)}
        </div>
      ) : null}
    </div>
  );
}

interface AutoMapHuntPatrolRuntime {
  enabled: boolean;
  state: string;
  mapID: string;
  mapDataID: string;
  targetLabel: string;
  targetXY: string;
  coveragePercent: number | null;
  exploredCount: number | null;
  totalCoverableCount: number | null;
  candidateCount: number | null;
}

function AutoMapHuntPatrolRuntimePanel({
  patrol,
}: {
  patrol: AutoMapHuntPatrolRuntime | null;
}) {
  if (!patrol || !patrol.enabled) return null;
  const coverageLabel =
    patrol.coveragePercent === null ? "—" : `${Math.round(patrol.coveragePercent)}%`;
  const exploredLabel =
    patrol.exploredCount === null || patrol.totalCoverableCount === null
      ? "—"
      : `${patrol.exploredCount}/${patrol.totalCoverableCount}`;
  return (
    <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-3 text-xs">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <StatusBadge variant="ok">视野覆盖</StatusBadge>
        {patrol.state ? <StatusBadge variant="neutral">{patrol.state}</StatusBadge> : null}
        {patrol.mapID ? <span className="font-mono">{patrol.mapID}</span> : null}
        {patrol.mapDataID ? (
          <span className="font-mono text-[hsl(var(--muted-foreground))]">
            mapDataID={patrol.mapDataID}
          </span>
        ) : null}
      </div>
      <div className="grid gap-2 md:grid-cols-4">
        <Metric label="覆盖率" value={coverageLabel} />
        <Metric label="已探索/可覆盖" value={exploredLabel} />
        <Metric
          label="候选点"
          value={patrol.candidateCount === null ? "—" : String(patrol.candidateCount)}
        />
        <Metric
          label="当前目标"
          value={patrol.targetLabel || patrol.targetXY || "—"}
          mono={Boolean(patrol.targetXY && !patrol.targetLabel)}
        />
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className={mono ? "font-mono" : "font-medium"}>{value}</div>
    </div>
  );
}

function parseAutoMapHuntPatrolRuntime(value: unknown): AutoMapHuntPatrolRuntime | null {
  const record = asRecord(value);
  if (!record) return null;
  const target = asRecord(record.targetPoint);
  const targetX = numberOrString(target?.x);
  const targetY = numberOrString(target?.y);
  const coverage = numberValue(record.coverageRatio);
  const candidatePoints = Array.isArray(record.candidatePoints)
    ? record.candidatePoints
    : null;
  return {
    enabled: record.enabled === true,
    state: stringValue(record.state),
    mapID: stringValue(record.mapID),
    mapDataID: stringValue(record.mapDataID),
    targetLabel: stringValue(target?.label),
    targetXY: targetX && targetY ? `${targetX},${targetY}` : "",
    coveragePercent: coverage === null ? null : coverage * 100,
    exploredCount: integerValue(record.exploredCount),
    totalCoverableCount: integerValue(record.totalCoverableCount),
    candidateCount: candidatePoints ? candidatePoints.length : integerValue(record.candidateCount),
  };
}

type PatrolTemplateDraftRecord = Record<string, unknown> & { id: string; status?: string };

function parsePatrolTemplateDraftFromRuntime(raw: string | undefined): PatrolTemplateDraftRecord | null {
  const record = parseRuntimeJSON(raw);
  if (typeof record.id !== "string" || !record.id.trim() || !Array.isArray(record.points)) {
    return null;
  }
  return record as PatrolTemplateDraftRecord;
}

function appendPatrolTemplateDraft(current: unknown, draft: PatrolTemplateDraftRecord) {
  const templates = Array.isArray(current)
    ? current.flatMap((item) => {
        const record = asRecord(item);
        return record ? [record] : [];
      })
    : [];
  const draftID = String(draft.id || "").trim();
  const nextDraft: PatrolTemplateDraftRecord = {
    ...draft,
    assistedCruiseRandomFly: asRecord(draft.assistedCruiseRandomFly) ?? { enabled: false },
    status: typeof draft.status === "string" && draft.status ? draft.status : "pending_confirm",
  };
  const existingIndex = templates.findIndex((item) => String(item.id || "") === draftID);
  if (existingIndex < 0) {
    return [...templates, nextDraft];
  }
  const existing = templates[existingIndex];
  if (String(existing.status || "") !== "pending_confirm") {
    const generatedAt = Number(draft.generatedAt || Date.now());
    nextDraft.id = `${draftID}_v${generatedAt}`;
    return [...templates, nextDraft];
  }
  return templates.map((item, index) => (index === existingIndex ? nextDraft : item));
}

interface InstanceContinentFleeCardProps {
  instanceId: string;
  data: InstanceDetailResponse;
  canWrite: boolean;
}

function InstanceContinentFleeCard({
  instanceId,
  data,
  canWrite,
}: InstanceContinentFleeCardProps) {
  const queryClient = useQueryClient();
  const config = data.module_configs?.continent_flee;

  const mut = useMutation({
    mutationFn: ({
      action,
      desired,
    }: {
      action: "save" | "save_and_apply" | "clear" | "clear_and_apply";
      desired: string;
    }) =>
      apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, "continent_flee"),
        { desired_config_json: desired, action },
      ),
    onSuccess: () => {
      toast.success("回城保护实例配置已保存");
      void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
    },
    onError: (err) => {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("保存回城保护配置失败", { description: msg });
    },
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>回城保护实例配置</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ModuleLayerBadges config={config} />
        <ContinentFleeConfigForm
          rawConfig={config?.desired_config_json || "{}"}
          scope="instance"
          disabled={!canWrite || mut.isPending}
          footer={
            canWrite ? (
              <ModuleActionButtons
                online={data.online}
                hasOverride={Boolean(config?.instance_override)}
                pending={mut.isPending}
                onAction={(action, desired) => mut.mutate({ action, desired })}
              />
            ) : null
          }
          onSubmit={(desired, action) =>
            mut.mutate({ action: moduleActionFromSubmit(action), desired })
          }
        />
      </CardContent>
    </Card>
  );
}

function InstanceTradingBankCard({
  instanceId,
  data,
  canWrite,
}: {
  instanceId: string;
  data: InstanceDetailResponse;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const config = data.module_configs?.trading_bank_assistant;
  const [targetRoleName, setTargetRoleName] = useState("");
  const [phone, setPhone] = useState(data.account?.phone ?? "");
  const [smsCode, setSmsCode] = useState("");
  const [batchRows, setBatchRows] = useState<TradingBankBatchRow[]>([]);

  const command = useMutation({
    mutationFn: (action: TradingBankRuntimeAction) => {
      const payload =
        action === "confirm_batch_equip_listing"
          ? {
              phone: phone.trim(),
              code: smsCode.trim(),
              target_rolename: targetRoleName.trim(),
              items: batchRows
                .map((row) => ({
                  make_index: row.make_index,
                  price: row.price.trim(),
                  target_rolename: row.target_rolename.trim(),
                }))
                .filter((row) => row.make_index !== "" && row.price !== ""),
            }
          : action === "send_phone_code"
            ? { phone: phone.trim() }
            : action === "refresh_market_state"
              ? { phone: phone.trim(), code: smsCode.trim() }
            : {};
      return apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, "trading_bank_assistant"),
        {
          action,
          payload_json: JSON.stringify(payload),
        },
      );
    },
    onSuccess: (response, action) => {
      toast.success(tradingBankActionToastLabel(action));
      queryClient.setQueryData<InstanceDetailResponse>(
        ["instance", instanceId],
        (current) => mergeTradingBankRuntime(current, response.config),
      );
      void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
    },
    onError: (err) => {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("交易行助手命令失败", { description: msg });
    },
  });

  const retrieveListingCommand = useMutation({
    mutationFn: (listing: TradingBankListingSummary) =>
      apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, "trading_bank_assistant"),
        {
          action: "retrieve_listing",
          payload_json: JSON.stringify({
            commodity_id: listing.commodity_id,
            status: listing.status,
            equip_id: listing.equip_id,
            phone: phone.trim(),
            code: smsCode.trim(),
          }),
        },
      ),
    onSuccess: (response) => {
      toast.success("交易行取回已发起");
      queryClient.setQueryData<InstanceDetailResponse>(
        ["instance", instanceId],
        (current) => mergeTradingBankRuntime(current, response.config),
      );
      void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
    },
    onError: (err) => {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("交易行取回失败", { description: msg });
    },
  });

  const runtime = parseRuntimeJSON(config?.runtime_status_json);
  const sellable = Array.isArray(runtime.sellable)
    ? runtime.sellable
        .filter(isTradingBankItem)
        .sort(compareTradingBankItemsByName)
    : [];
  const myListings = Array.isArray(runtime.my_listings)
    ? runtime.my_listings
        .filter(isTradingBankListing)
        .sort(compareTradingBankListingsByName)
    : [];
  const activeItem = isTradingBankItem(runtime.item) ? runtime.item : null;
  const safeArea =
    typeof runtime.safe_area === "boolean" ? runtime.safe_area : undefined;
  const runtimeStatus =
    typeof runtime.status === "string" && runtime.status.trim()
      ? runtime.status
      : config?.runtime_status;
  const lastError =
    typeof runtime.last_error === "string" && runtime.last_error.trim()
      ? runtime.last_error
      : config?.last_command_error;
  const nextStep =
    typeof runtime.next_step === "string" ? runtime.next_step.trim() : "";
  const phoneBound =
    typeof runtime.phone_bound === "boolean" ? runtime.phone_bound : undefined;
  const phoneMasked =
    typeof runtime.phone_masked === "string" ? runtime.phone_masked.trim() : "";
  const tokenReady = runtime.token_ready === true;
  const runtimePrice =
    typeof runtime.price === "string" || typeof runtime.price === "number"
      ? String(runtime.price)
      : "";
  const activeItemLabel =
    runtimeStatus === "error" || runtimeStatus === "batch_error"
      ? "上次执行"
      : "当前执行";
  const shouldPollRuntime = isTradingBankBusyStatus(runtimeStatus);

  useEffect(() => {
    if (tokenReady && smsCode.trim()) {
      setSmsCode("");
    }
  }, [smsCode, tokenReady]);

  useEffect(() => {
    if (!data.online || !shouldPollRuntime) return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      void apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, "trading_bank_assistant"),
        { action: "query_status", payload_json: "{}" },
      )
        .then(() => {
          if (!cancelled) {
            void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
          }
        })
        .catch(() => {
          // 静默轮询只用于刷新异步上架状态，失败时保留页面当前错误展示。
        });
    }, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [data.online, instanceId, queryClient, shouldPollRuntime]);

  function addBatchRow(item: TradingBankItemSummary) {
    const makeIndexValue = String(item.make_index ?? "");
    if (!makeIndexValue) return;
    setBatchRows((prev) => {
      if (prev.some((row) => row.make_index === makeIndexValue)) return prev;
      return [
        ...prev,
        {
          make_index: makeIndexValue,
          name: item.name || "未命名",
          item_id: item.item_id ?? "",
          overlap: item.overlap,
          price: "",
          target_rolename: targetRoleName,
        },
      ];
    });
  }

  function updateBatchRow(
    makeIndexValue: string,
    patch: Partial<Pick<TradingBankBatchRow, "price" | "target_rolename">>,
  ) {
    setBatchRows((prev) =>
      prev.map((row) =>
        row.make_index === makeIndexValue ? { ...row, ...patch } : row,
      ),
    );
  }

  const batchReadyCount = batchRows.filter((row) => row.price.trim()).length;
  const batchStatus = parseTradingBankBatch(runtime.batch);
  const batchResultsByMakeIndex = new Map<string, TradingBankBatchResult>();
  for (const result of batchStatus?.results ?? []) {
    if (result?.make_index) {
      batchResultsByMakeIndex.set(result.make_index, result);
    }
  }
  const completedBatchMakeIndexes = new Set(
    [...batchResultsByMakeIndex.values()]
      .filter((result) => result.ok)
      .map((result) => result.make_index),
  );
  const smsCodeValue = smsCode.trim();
  const needsSmsCode = !tokenReady;
  const missingSmsCode = needsSmsCode && !smsCodeValue;
  const confirmDisabled =
    command.isPending ||
    !data.online ||
    batchReadyCount === 0 ||
    batchReadyCount !== batchRows.length ||
    missingSmsCode;
  const confirmHint = !data.online
    ? "当前实例离线，不能上架。"
    : batchRows.length === 0
      ? ""
      : batchReadyCount !== batchRows.length
        ? "请先给队列中每件装备填写价格。"
        : missingSmsCode
          ? "近期未完成手机验证，请先发送验证码并填写短信验证码。"
          : needsSmsCode
            ? "确认后会先后台校验短信验证码，校验通过再继续上架。"
            : "近期手机验证仍有效，可直接确认上架。";
  const retrieveNeedsSmsCode = !tokenReady;
  const refreshNeedsSmsCode = !tokenReady;
  const refreshDisabled =
    command.isPending ||
    retrieveListingCommand.isPending ||
    !data.online ||
    (refreshNeedsSmsCode && !smsCodeValue);
  const refreshHint = !data.online
    ? "当前实例离线，不能刷新货架。"
    : refreshNeedsSmsCode && !smsCodeValue
      ? "刷新货架需要先发送验证码并填写短信验证码。"
      : refreshNeedsSmsCode
        ? "会先后台校验短信验证码，校验通过后刷新货架。"
        : "刷新当前交易行货架。";
  const retrieveDisabled = (listing: TradingBankListingSummary) => {
    const listingStatus = Number(listing.status);
    return (
      retrieveListingCommand.isPending ||
      command.isPending ||
      !data.online ||
      (retrieveNeedsSmsCode && !smsCodeValue) ||
      listingStatus === 3
    );
  };
  const retrieveHint = (listing: TradingBankListingSummary) => {
    const listingStatus = Number(listing.status);
    if (!data.online) return "当前实例离线，不能取回。";
    if (listingStatus === 3) return "商品出售中，原版交易行不允许取回。";
    if (retrieveNeedsSmsCode && !smsCodeValue) {
      return "近期未完成手机验证，请先发送验证码并填写短信验证码。";
    }
    if (listingStatus === 1) return "会先下架，再取回并尝试收取对应邮件附件。";
    return "会直接取回并尝试收取对应邮件附件。";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Store className="h-4 w-4" />
          交易行装备上架
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 text-xs">
          <StatusBadge variant={data.online ? "ok" : "neutral"}>
            {data.online ? "当前实例在线" : "实例离线"}
          </StatusBadge>
          {safeArea !== undefined ? (
            <StatusBadge variant={safeArea ? "ok" : "warn"}>
              {safeArea ? "安全区" : "非安全区"}
            </StatusBadge>
          ) : null}
          {runtimeStatus ? (
            <StatusBadge variant={lastError ? "danger" : "neutral"}>
              {formatTradingBankRuntimeStatus(runtimeStatus)}
            </StatusBadge>
          ) : null}
          {phoneBound !== undefined ? (
            <StatusBadge variant={phoneBound ? "ok" : "warn"}>
              {phoneBound ? "已绑定手机" : "未绑定手机"}
            </StatusBadge>
          ) : null}
          {tokenReady ? <StatusBadge variant="ok">验证码已通过</StatusBadge> : null}
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <Field label={phoneBound === false ? "手机号" : "手机号"}>
            <Input
              value={phone}
              disabled={!canWrite}
              onChange={(event) => setPhone(event.target.value)}
              placeholder={phoneMasked || "已绑定账号可留空"}
            />
          </Field>
          <Field label="短信验证码">
            <Input
              value={smsCode}
              disabled={!canWrite}
              onChange={(event) => setSmsCode(event.target.value)}
              inputMode="numeric"
            />
          </Field>
          <Field label="默认指定购买人（可选）">
            <Input
              value={targetRoleName}
              disabled={!canWrite}
              onChange={(event) => setTargetRoleName(event.target.value)}
            />
          </Field>
        </div>

        {lastError ? (
          <div className="rounded border border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/10 p-3 text-xs text-[hsl(var(--destructive))]">
            {lastError}
          </div>
        ) : null}

        {nextStep ? (
          <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-3 text-xs">
            {nextStep}
          </div>
        ) : null}

        {activeItem ? (
          <div className="rounded border border-[hsl(var(--border))] p-3 text-xs">
            {activeItemLabel}：{activeItem.name || "未命名"}（ID {activeItem.item_id ?? "—"}，
            MakeIndex {activeItem.make_index ?? "—"}，价格 {runtimePrice || "—"}）
          </div>
        ) : null}

        <div className="overflow-auto rounded border border-[hsl(var(--border))]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>当前货架</TableHead>
                <TableHead>商品 ID</TableHead>
                <TableHead>物品 ID</TableHead>
                <TableHead>数量</TableHead>
                <TableHead>价格</TableHead>
                <TableHead>状态</TableHead>
                {canWrite ? <TableHead className="w-24">操作</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {myListings.length > 0 ? (
                myListings.map((listing) => (
                  <TableRow key={String(listing.commodity_id)}>
                    <TableCell>{listing.name || "未命名"}</TableCell>
                    <TableCell className="font-mono">
                      {listing.commodity_id ?? "—"}
                    </TableCell>
                    <TableCell className="font-mono">{listing.equip_id ?? "—"}</TableCell>
                    <TableCell>{formatTradingBankItemCount(listing.equip_num)}</TableCell>
                    <TableCell className="font-mono">{listing.price ?? "—"}</TableCell>
                    <TableCell>
                      <StatusBadge variant={tradingBankListingStatusVariant(listing.status)}>
                        {formatTradingBankListingStatus(listing)}
                      </StatusBadge>
                    </TableCell>
                    {canWrite ? (
                      <TableCell>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={retrieveDisabled(listing)}
                          title={retrieveHint(listing)}
                          onClick={() => retrieveListingCommand.mutate(listing)}
                        >
                          <Download className="mr-1 h-3.5 w-3.5" />
                          取回
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={canWrite ? 7 : 6}
                    className="text-[hsl(var(--muted-foreground))]"
                  >
                    暂无货架数据，点击“刷新状态”获取当前已上架装备。
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {sellable.length > 0 ? (
          <div className="overflow-auto rounded border border-[hsl(var(--border))]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>装备</TableHead>
                  <TableHead>物品 ID</TableHead>
                  <TableHead>MakeIndex</TableHead>
                  <TableHead>数量</TableHead>
                  {canWrite ? <TableHead className="w-24">操作</TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sellable.map((item) => (
                  <TableRow key={`${item.make_index ?? ""}:${item.item_id ?? ""}`}>
                    <TableCell>{item.name || "未命名"}</TableCell>
                    <TableCell className="font-mono">{item.item_id ?? "—"}</TableCell>
                    <TableCell className="font-mono">{item.make_index ?? "—"}</TableCell>
                    <TableCell>{item.overlap ?? "—"}</TableCell>
                    {canWrite ? (
                      <TableCell>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => addBatchRow(item)}
                        >
                          <UploadCloud className="mr-1 h-3.5 w-3.5" />
                          加批量
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : null}

        {batchRows.length > 0 ? (
          <div className="overflow-auto rounded border border-[hsl(var(--border))]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>待上架装备</TableHead>
                  <TableHead>数量</TableHead>
                  <TableHead>MakeIndex</TableHead>
                  <TableHead>价格</TableHead>
                  <TableHead>指定购买人</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead className="w-16">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batchRows.map((row) => {
                  const rowResult = batchResultsByMakeIndex.get(row.make_index);
                  return (
                    <TableRow key={row.make_index}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{formatTradingBankItemCount(row.overlap)}</TableCell>
                      <TableCell className="font-mono">{row.make_index}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min={1}
                          step={1}
                          value={row.price}
                          disabled={!canWrite}
                          onChange={(event) =>
                            updateBatchRow(row.make_index, { price: event.target.value })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={row.target_rolename}
                          disabled={!canWrite}
                          onChange={(event) =>
                            updateBatchRow(row.make_index, {
                              target_rolename: event.target.value,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {rowResult ? (
                          <div className="space-y-1">
                            <StatusBadge variant={rowResult.ok ? "ok" : "danger"}>
                              {rowResult.ok ? "已上架" : "失败"}
                            </StatusBadge>
                            {!rowResult.ok && rowResult.error ? (
                              <div className="max-w-48 text-[hsl(var(--destructive))]">
                                {rowResult.error}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-[hsl(var(--muted-foreground))]">
                            待提交
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={!canWrite}
                          onClick={() =>
                            setBatchRows((prev) =>
                              prev.filter((item) => item.make_index !== row.make_index),
                            )
                          }
                        >
                          移除
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[hsl(var(--border))] p-2 text-xs">
              <div className="min-w-0">
                <div>
                  上架队列 {batchReadyCount}/{batchRows.length}
                  {batchStatus ? `，进度 ${batchStatus.index}/${batchStatus.total}` : ""}
                </div>
                {confirmHint ? (
                  <div
                    className={
                      missingSmsCode
                        ? "text-[hsl(var(--warn))]"
                        : "text-[hsl(var(--muted-foreground))]"
                    }
                  >
                    {confirmHint}
                  </div>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                {completedBatchMakeIndexes.size > 0 ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!canWrite}
                    onClick={() =>
                      setBatchRows((prev) =>
                        prev.filter(
                          (row) => !completedBatchMakeIndexes.has(row.make_index),
                        ),
                      )
                    }
                  >
                    移除已完成
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  disabled={confirmDisabled}
                  title={confirmHint || undefined}
                  onClick={() => command.mutate("confirm_batch_equip_listing")}
                >
                  <UploadCloud className="mr-1 h-3.5 w-3.5" />
                  确认上架
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {canWrite ? (
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={refreshDisabled}
              title={refreshHint}
              onClick={() => command.mutate("refresh_market_state")}
            >
              <RefreshCw className="mr-1 h-3.5 w-3.5" />
              刷新状态
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={command.isPending || retrieveListingCommand.isPending || !data.online}
              onClick={() => command.mutate("list_sellable_equips")}
            >
              <Search className="mr-1 h-3.5 w-3.5" />
              扫描可上架装备
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={command.isPending || retrieveListingCommand.isPending || !data.online}
              onClick={() => command.mutate("open_online_recycle")}
            >
              <Recycle className="mr-1 h-3.5 w-3.5" />
              在线回收
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={command.isPending || retrieveListingCommand.isPending || !data.online}
              onClick={() => command.mutate("send_phone_code")}
            >
              <Send className="mr-1 h-3.5 w-3.5" />
              发送验证码
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function InstanceDailyCard({
  instanceId,
  data,
  canWrite,
}: {
  instanceId: string;
  data: InstanceDetailResponse;
  canWrite: boolean;
}) {
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <InstanceDailyGiftCard
        instanceId={instanceId}
        data={data}
        canWrite={canWrite}
      />
      <InstanceWorldTreeCard
        instanceId={instanceId}
        data={data}
        canWrite={canWrite}
      />
    </div>
  );
}

function InstanceDailyGiftCard({
  instanceId,
  data,
  canWrite,
}: {
  instanceId: string;
  data: InstanceDetailResponse;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const config = data.module_configs?.box996_daily_gift;
  const runtime = parseRuntimeJSON(config?.runtime_status_json);
  const dailyCompletion = config?.daily_completion;
  const dailyCompleted = dailyCompletion?.completed === true;
  const runtimeStatus =
    typeof runtime.status === "string" && runtime.status.trim()
      ? runtime.status
      : config?.runtime_status;
  const lastError =
    typeof runtime.last_error === "string" && runtime.last_error.trim()
      ? runtime.last_error
      : config?.last_command_error;
  const nextStep =
    typeof runtime.next_step === "string" ? runtime.next_step.trim() : "";
  const isBoxOpen = runtime.is_box_open === true;
  const claimed = runtime.claimed === true;
  const claimResult = isDailyGiftClaimResult(runtime.claim_result)
    ? runtime.claim_result
    : null;
  const mailCollect = isDailyGiftMailCollect(runtime.mail_collect)
    ? runtime.mail_collect
    : null;
  const shouldPollRuntime = isDailyGiftBusyStatus(runtimeStatus);

  const command = useMutation({
    mutationFn: (action: DailyGiftRuntimeAction) =>
      apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, "box996_daily_gift"),
        {
          action,
          payload_json: "{}",
        },
      ),
    onSuccess: (response, action) => {
      toast.success(dailyGiftActionToastLabel(action));
      queryClient.setQueryData<InstanceDetailResponse>(
        ["instance", instanceId],
        (current) => mergeDailyGiftRuntime(current, response.config),
      );
      void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
    },
    onError: (err) => {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("每日礼包命令失败", { description: msg });
    },
  });

  useEffect(() => {
    if (!data.online || !shouldPollRuntime) return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      void apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, "box996_daily_gift"),
        { action: "query_status", payload_json: "{}" },
      )
        .then((response) => {
          if (!cancelled) {
            queryClient.setQueryData<InstanceDetailResponse>(
              ["instance", instanceId],
              (current) => mergeDailyGiftRuntime(current, response.config),
            );
          }
        })
        .catch(() => {
          // 静默轮询只用于刷新异步领取/邮件收取状态。
        });
    }, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [data.online, instanceId, queryClient, shouldPollRuntime]);

  const statusVariant = lastError
    ? "danger"
    : dailyCompleted || claimed || runtimeStatus === "claimed_mail_collected" || runtimeStatus === "daily_completed"
      ? "ok"
      : isBoxOpen
        ? "neutral"
        : "warn";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gift className="h-4 w-4" />
          每日礼包
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 text-xs">
          <StatusBadge variant={data.online ? "ok" : "neutral"}>
            {data.online ? "当前实例在线" : "实例离线"}
          </StatusBadge>
          <StatusBadge variant={isBoxOpen ? "ok" : "warn"}>
            {isBoxOpen ? "盒子登录态可用" : "盒子登录态未知"}
          </StatusBadge>
          <StatusBadge variant={claimed ? "ok" : "neutral"}>
            {dailyCompleted || claimed ? "今日已完成" : "今日未领取"}
          </StatusBadge>
          {runtimeStatus ? (
            <StatusBadge variant={statusVariant}>
              {formatDailyGiftRuntimeStatus(runtimeStatus)}
            </StatusBadge>
          ) : null}
        </div>

        {lastError ? (
          <div className="rounded border border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/10 p-3 text-xs text-[hsl(var(--destructive))]">
            {lastError}
          </div>
        ) : null}

        {nextStep ? (
          <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-3 text-xs">
            {nextStep}
          </div>
        ) : null}

        {dailyCompletion ? (
          <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-3 text-xs">
            每日进度：{dailyCompleted ? "已完成" : "未完成"}（业务日 {dailyCompletion.business_day}，
            {dailyCompletion.reset_hour}:00 重置）
          </div>
        ) : null}

        {claimResult ? (
          <div className="rounded border border-[hsl(var(--border))] p-3 text-xs">
            服务端回包：{claimResult.message || "—"}（code {claimResult.code ?? "—"}）
          </div>
        ) : null}

        {mailCollect?.status ? (
          <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-3 text-xs">
            邮件附件：{formatDailyGiftMailStatus(mailCollect.status)}
            {mailCollect.requested_count !== undefined
              ? `，已请求 ${mailCollect.requested_count} 封`
              : ""}
          </div>
        ) : null}

        {canWrite ? (
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={command.isPending || !data.online}
              onClick={() => command.mutate("query_status")}
            >
              <RefreshCw className="mr-1 h-3.5 w-3.5" />
              刷新状态
            </Button>
            <Button
              size="sm"
              disabled={command.isPending || !data.online || shouldPollRuntime || dailyCompleted}
              title={!data.online ? "当前实例离线，不能领取。" : undefined}
              onClick={() => command.mutate("claim_daily_gift")}
            >
              <Gift className="mr-1 h-3.5 w-3.5" />
              一键领取每日礼包
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function InstanceWorldTreeCard({
  instanceId,
  data,
  canWrite,
}: {
  instanceId: string;
  data: InstanceDetailResponse;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const config = data.module_configs?.auto_world_tree;
  const runtime = parseRuntimeJSON(config?.runtime_status_json);
  const dailyCompletion = config?.daily_completion;
  const dailyCompleted = dailyCompletion?.completed === true;
  const runtimeStatus =
    typeof runtime.status === "string" && runtime.status.trim()
      ? runtime.status
      : config?.runtime_status;
  const lastError =
    typeof runtime.error === "string" && runtime.error.trim()
      ? runtime.error
      : config?.last_command_error;
  const statusMessage =
    typeof runtime.message === "string" && runtime.message.trim()
      ? runtime.message
      : typeof runtime.status === "string" && runtime.status.trim()
        ? runtime.status
        : "";
  const running = runtime.running === true || runtimeStatus === "running";
  const interrupted = runtimeStatus === "interrupted" || runtime.resultStatus === "interrupted";
  const map = isRecord(runtime.map) ? runtime.map : {};
  const position = isRecord(runtime.position) ? runtime.position : {};
  const mapName =
    typeof map.name === "string" && map.name.trim()
      ? map.name
      : typeof map.id === "string"
        ? map.id
        : "—";
  const posText =
    typeof position.x !== "undefined" && typeof position.y !== "undefined"
      ? `${position.x},${position.y}`
      : "—";
  const essenceCount =
    typeof runtime.essenceCount === "number" || typeof runtime.essenceCount === "string"
      ? String(runtime.essenceCount)
      : "0";
  const upgradeClicks =
    typeof runtime.upgradeClicks === "number" || typeof runtime.upgradeClicks === "string"
      ? String(runtime.upgradeClicks)
      : "0";
  const completedDate =
    typeof runtime.completedDate === "string" && runtime.completedDate.trim()
      ? runtime.completedDate
      : dailyCompletion?.business_day || "—";
  const interruptedCount =
    typeof runtime.interruptedCount === "number" || typeof runtime.interruptedCount === "string"
      ? String(runtime.interruptedCount)
      : "0";
  const returnHomeRequestCount =
    typeof runtime.returnHomeRequestCount === "number" ||
    typeof runtime.returnHomeRequestCount === "string"
      ? String(runtime.returnHomeRequestCount)
      : "0";

  const command = useMutation({
    mutationFn: (action: WorldTreeRuntimeAction) =>
      apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, "auto_world_tree"),
        {
          action,
          payload_json: "{}",
        },
      ),
    onSuccess: (response, action) => {
      toast.success(worldTreeActionToastLabel(action));
      queryClient.setQueryData<InstanceDetailResponse>(
        ["instance", instanceId],
        (current) => mergeWorldTreeRuntime(current, response.config),
      );
      void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
    },
    onError: (err) => {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("世界树命令失败", { description: msg });
    },
  });

  useEffect(() => {
    if (!data.online || !running) return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      void apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, "auto_world_tree"),
        { action: "query_status", payload_json: "{}" },
      )
        .then((response) => {
          if (!cancelled) {
            queryClient.setQueryData<InstanceDetailResponse>(
              ["instance", instanceId],
              (current) => mergeWorldTreeRuntime(current, response.config),
            );
          }
        })
        .catch(() => {
          // 静默轮询只用于刷新世界树流程状态。
        });
    }, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [data.online, instanceId, queryClient, running]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Play className="h-4 w-4" />
          世界树
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 text-xs">
          <StatusBadge variant={data.online ? "ok" : "neutral"}>
            {data.online ? "当前实例在线" : "实例离线"}
          </StatusBadge>
          <StatusBadge variant={running ? "ok" : "neutral"}>
            {running ? "执行中" : dailyCompleted ? "今日已完成" : "空闲"}
          </StatusBadge>
          {runtimeStatus ? (
            <StatusBadge variant={lastError ? "danger" : interrupted ? "warn" : running ? "ok" : "neutral"}>
              {formatWorldTreeRuntimeStatus(runtimeStatus)}
            </StatusBadge>
          ) : null}
        </div>

        {lastError ? (
          <div className="rounded border border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/10 p-3 text-xs text-[hsl(var(--destructive))]">
            {lastError}
          </div>
        ) : null}

        {statusMessage && statusMessage !== runtimeStatus ? (
          <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-3 text-xs">
            {statusMessage}
          </div>
        ) : null}

        <dl className="grid gap-2 text-xs sm:grid-cols-2">
          <div className="rounded border border-[hsl(var(--border))] p-3">
            <dt className="text-[hsl(var(--muted-foreground))]">地图</dt>
            <dd>{mapName}</dd>
          </div>
          <div className="rounded border border-[hsl(var(--border))] p-3">
            <dt className="text-[hsl(var(--muted-foreground))]">坐标</dt>
            <dd className="font-mono">{posText}</dd>
          </div>
          <div className="rounded border border-[hsl(var(--border))] p-3">
            <dt className="text-[hsl(var(--muted-foreground))]">世界树精华</dt>
            <dd>{essenceCount}</dd>
          </div>
          <div className="rounded border border-[hsl(var(--border))] p-3">
            <dt className="text-[hsl(var(--muted-foreground))]">升级点击</dt>
            <dd>{upgradeClicks}</dd>
          </div>
          <div className="rounded border border-[hsl(var(--border))] p-3">
            <dt className="text-[hsl(var(--muted-foreground))]">回城保护中断</dt>
            <dd>{interruptedCount}</dd>
          </div>
          <div className="rounded border border-[hsl(var(--border))] p-3">
            <dt className="text-[hsl(var(--muted-foreground))]">回盟重请求</dt>
            <dd>{returnHomeRequestCount}</dd>
          </div>
        </dl>

        <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-3 text-xs">
          今日完成：{dailyCompleted ? completedDate : "未完成"}
          {dailyCompletion ? `，${dailyCompletion.reset_hour}:00 重置` : ""}
        </div>

        {canWrite ? (
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={command.isPending || !data.online}
              onClick={() => command.mutate("query_status")}
            >
              <RefreshCw className="mr-1 h-3.5 w-3.5" />
              刷新状态
            </Button>
            <Button
              size="sm"
              disabled={command.isPending || !data.online || running || dailyCompleted}
              title={!data.online ? "当前实例离线，不能执行。" : undefined}
              onClick={() => command.mutate("run_world_tree")}
            >
              <Play className="mr-1 h-3.5 w-3.5" />
              执行世界树
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

interface InstanceOtherModulesCardProps {
  instanceId: string;
  data: InstanceDetailResponse;
  canWrite: boolean;
}

function InstanceOtherModulesCard({
  instanceId,
  data,
  canWrite,
}: InstanceOtherModulesCardProps) {
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <InstanceAutoSummonCard
        instanceId={instanceId}
        data={data}
        canWrite={canWrite}
      />
      <InstanceAutoSuoYaoCard
        instanceId={instanceId}
        data={data}
        canWrite={canWrite}
      />
      <InstanceAutoWuxingCard
        instanceId={instanceId}
        data={data}
        canWrite={canWrite}
      />
      <InstancePullMonsterCard
        instanceId={instanceId}
        data={data}
        canWrite={canWrite}
      />
      <InstanceAutoAuctionBidCard
        instanceId={instanceId}
        data={data}
        canWrite={canWrite}
      />
    </div>
  );
}

function InstanceAutoAuctionBidCard({
  instanceId,
  data,
  canWrite,
}: {
  instanceId: string;
  data: InstanceDetailResponse;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const config = data.module_configs?.auto_auction_bid;
  const runtime = useInstanceModuleRuntimeCommand({
    instanceId,
    module: "auto_auction_bid",
    label: "拍卖竞价",
  });

  const mut = useMutation({
    mutationFn: ({
      action,
      desired,
    }: {
      action: "save" | "save_and_apply" | "clear" | "clear_and_apply";
      desired: string;
    }) =>
      apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, "auto_auction_bid"),
        { desired_config_json: desired, action },
      ),
    onSuccess: () => {
      toast.success("拍卖竞价实例配置已保存");
      void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
    },
    onError: (err) => {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("保存拍卖竞价配置失败", { description: msg });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>拍卖竞价</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ModuleRuntimeControls
          online={data.online}
          canWrite={canWrite}
          status={config?.runtime_status}
          lastError={config?.last_command_error}
          pending={runtime.isPending}
          onAction={(action) => runtime.mutate(action)}
        />
        <ModuleLayerBadges config={config} />
        <AutoAuctionBidConfigForm
          rawConfig={config?.desired_config_json || "{}"}
          scope="instance"
          disabled={!canWrite || mut.isPending}
          footer={
            canWrite ? (
              <ModuleActionButtons
                online={data.online}
                hasOverride={Boolean(config?.instance_override)}
                pending={mut.isPending}
                onAction={(action, desired) => mut.mutate({ action, desired })}
              />
            ) : null
          }
          onSubmit={(desired, action) =>
            mut.mutate({ action: moduleActionFromSubmit(action), desired })
          }
        />
      </CardContent>
    </Card>
  );
}

function InstanceAutoSummonCard({
  instanceId,
  data,
  canWrite,
}: {
  instanceId: string;
  data: InstanceDetailResponse;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const config = data.module_configs?.auto_summon;

  const mut = useMutation({
    mutationFn: ({
      action,
      desired,
    }: {
      action: "save" | "save_and_apply" | "clear" | "clear_and_apply";
      desired: string;
    }) =>
      apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, "auto_summon"),
        { desired_config_json: desired, action },
      ),
    onSuccess: () => {
      toast.success("智能召唤实例配置已保存");
      void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
    },
    onError: (err) => {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("保存智能召唤配置失败", { description: msg });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>智能召唤</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ModuleLayerBadges config={config} />
        <AutoSummonConfigForm
          rawConfig={config?.desired_config_json || "{}"}
          scope="instance"
          disabled={!canWrite || mut.isPending}
          footer={
            canWrite ? (
              <ModuleActionButtons
                online={data.online}
                hasOverride={Boolean(config?.instance_override)}
                pending={mut.isPending}
                onAction={(action, desired) => mut.mutate({ action, desired })}
              />
            ) : null
          }
          onSubmit={(desired, action) =>
            mut.mutate({ action: moduleActionFromSubmit(action), desired })
          }
        />
      </CardContent>
    </Card>
  );
}

function InstanceAutoSuoYaoCard({
  instanceId,
  data,
  canWrite,
}: {
  instanceId: string;
  data: InstanceDetailResponse;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const config = data.module_configs?.auto_suoyao;

  const mut = useMutation({
    mutationFn: ({
      action,
      desired,
    }: {
      action: "save" | "save_and_apply" | "clear" | "clear_and_apply";
      desired: string;
    }) =>
      apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, "auto_suoyao"),
        { desired_config_json: desired, action },
      ),
    onSuccess: () => {
      toast.success("自动锁妖实例配置已保存");
      void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
    },
    onError: (err) => {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("保存自动锁妖配置失败", { description: msg });
    },
  });
  const runtime = useInstanceModuleRuntimeCommand({
    instanceId,
    module: "auto_suoyao",
    label: "自动锁妖",
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>自动锁妖</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ModuleRuntimeControls
          online={data.online}
          canWrite={canWrite}
          status={config?.runtime_status}
          lastError={config?.last_command_error}
          pending={runtime.isPending}
          onAction={(action) => runtime.mutate(action)}
        />
        <ModuleLayerBadges config={config} />
        <AutoSuoYaoConfigForm
          rawConfig={config?.desired_config_json || "{}"}
          scope="instance"
          disabled={!canWrite || mut.isPending}
          footer={
            canWrite ? (
              <ModuleActionButtons
                online={data.online}
                hasOverride={Boolean(config?.instance_override)}
                pending={mut.isPending}
                onAction={(action, desired) => mut.mutate({ action, desired })}
              />
            ) : null
          }
          onSubmit={(desired, action) =>
            mut.mutate({ action: moduleActionFromSubmit(action), desired })
          }
        />
      </CardContent>
    </Card>
  );
}

function InstanceAutoWuxingCard({
  instanceId,
  data,
  canWrite,
}: {
  instanceId: string;
  data: InstanceDetailResponse;
  canWrite: boolean;
}) {
  const config = data.module_configs?.auto_wuxing;
  const runtime = useInstanceModuleRuntimeCommand({
    instanceId,
    module: "auto_wuxing",
    label: "自动五行",
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>自动五行</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ModuleRuntimeControls
          online={data.online}
          canWrite={canWrite}
          status={config?.runtime_status}
          lastError={config?.last_command_error}
          pending={runtime.isPending}
          onAction={(action) => runtime.mutate(action)}
        />
        <ModuleLayerBadges config={config} />
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          五行当前由通用自动设置面板承载，没有独立实例配置；这里仅控制当前实例运行态。
        </p>
      </CardContent>
    </Card>
  );
}

function InstancePullMonsterCard({
  instanceId,
  data,
  canWrite,
}: {
  instanceId: string;
  data: InstanceDetailResponse;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const config = data.module_configs?.full_screen_pull_monster;

  const mut = useMutation({
    mutationFn: ({
      action,
      desired,
    }: {
      action: "save" | "save_and_apply" | "clear" | "clear_and_apply";
      desired: string;
    }) =>
      apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, "full_screen_pull_monster"),
        { desired_config_json: desired, action },
      ),
    onSuccess: () => {
      toast.success("全屏吸怪实例配置已保存");
      void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
    },
    onError: (err) => {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("保存全屏吸怪配置失败", { description: msg });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>全屏吸怪</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ModuleLayerBadges config={config} />
        <PullMonsterConfigForm
          rawConfig={config?.desired_config_json || "{}"}
          scope="instance"
          disabled={!canWrite || mut.isPending}
          footer={
            canWrite ? (
              <ModuleActionButtons
                online={data.online}
                hasOverride={Boolean(config?.instance_override)}
                pending={mut.isPending}
                onAction={(action, desired) => mut.mutate({ action, desired })}
              />
            ) : null
          }
          onSubmit={(desired, action) =>
            mut.mutate({ action: moduleActionFromSubmit(action), desired })
          }
        />
      </CardContent>
    </Card>
  );
}

type ModuleAction = "save" | "save_and_apply" | "clear" | "clear_and_apply";
type ModuleRuntimeAction = "query_status" | "start" | "stop";

function emptyAutoMapHuntObservability(
  instanceId: string,
): AutoMapHuntObservabilityResponse {
  return {
    ok: true,
    instance_id: instanceId,
    generated_at: Date.now(),
    trace: {},
    recent_traces: [],
    not_selected: [],
    coordination: {
      schema: "hbh996.auto_map_hunt.coordination.v1",
      counts: {
        leases: 0,
        signals: 0,
        presence: 0,
        reservations: 0,
        preferred: 0,
      },
      leases: [],
      signals: [],
      presence: [],
      reservations: [],
      preferred: [],
    },
  };
}

function normalizeAutoMapHuntTrace(
  trace: AutoMapHuntObservabilityResponse["trace"] | undefined,
) {
  if (!trace || (!trace.event && !trace.selected && !trace.top_reject_reason)) {
    return null;
  }
  return trace;
}

function formatScore(value: number | undefined | null) {
  if (value === undefined || value === null || Number.isNaN(value)) return "0.0";
  return value.toFixed(1);
}

function formatArrivalETA(value: number | undefined | null) {
  if (value === undefined || value === null || Number.isNaN(value) || value <= 0) {
    return "—";
  }
  if (value >= 1000) {
    return `${Math.round(value / 1000)}s`;
  }
  return `${Math.round(value)}ms`;
}

function formatWallTime(ms: number | undefined | null) {
  if (!ms || Number.isNaN(ms)) return "—";
  try {
    return new Date(ms).toLocaleTimeString("zh-CN", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return String(ms);
  }
}

interface TradingBankItemSummary {
  make_index?: number | string;
  item_id?: number | string;
  name?: string;
  overlap?: number | string;
}

interface TradingBankListingSummary {
  commodity_id?: number | string;
  name?: string;
  type?: number | string;
  status?: number | string;
  status_ch?: string;
  price?: number | string;
  equip_id?: number | string;
  equip_num?: number | string;
  can_retrieve?: boolean;
  retrieve_requires_down?: boolean;
}

interface TradingBankBatchRow {
  make_index: string;
  item_id: number | string;
  name: string;
  overlap?: number | string;
  price: string;
  target_rolename: string;
}

interface TradingBankBatchStatus {
  index: number;
  total: number;
  results: TradingBankBatchResult[];
}

interface TradingBankBatchResult {
  ok: boolean;
  make_index: string;
  error: string;
}

type TradingBankRuntimeAction =
  | "query_status"
  | "list_sellable_equips"
  | "list_my_listings"
  | "refresh_market_state"
  | "open_online_recycle"
  | "retrieve_listing"
  | "send_phone_code"
  | "confirm_batch_equip_listing";

type DailyGiftRuntimeAction = "query_status" | "claim_daily_gift";
type WorldTreeRuntimeAction = "query_status" | "run_world_tree";

interface DailyGiftClaimResult {
  code?: number | string;
  message?: string;
}

interface DailyGiftMailCollect {
  status?: string;
  matched_count?: number | string;
  requested_count?: number | string;
}

function tradingBankActionToastLabel(action: TradingBankRuntimeAction) {
  switch (action) {
    case "list_sellable_equips":
      return "可上架装备扫描完成";
    case "list_my_listings":
    case "refresh_market_state":
      return "交易行状态已刷新";
    case "open_online_recycle":
      return "在线回收入口已触发";
    case "retrieve_listing":
      return "交易行取回已发起";
    case "send_phone_code":
      return "验证码发送已发起";
    case "confirm_batch_equip_listing":
      return "装备上架已发起";
    default:
      return "交易行助手状态已返回";
  }
}

function formatTradingBankRuntimeStatus(status: string) {
  switch (status) {
    case "ready":
      return "就绪";
    case "prepared":
      return "已准备";
    case "sending_code":
      return "发送验证码中";
    case "code_sent":
      return "验证码已发送";
    case "verifying_code":
      return "校验验证码中";
    case "verified":
      return "验证码已通过";
    case "validating_target_role":
      return "校验指定购买人";
    case "capturing":
      return "截图中";
    case "uploading_images":
      return "上传图片中";
    case "checking_item":
      return "校验装备中";
    case "submitting":
      return "提交上架中";
    case "listed":
      return "已上架";
    case "batch_prepared":
      return "批量已准备";
    case "batch_next":
      return "批量继续中";
    case "batch_listed":
      return "批量已完成";
    case "batch_error":
      return "批量失败";
    case "refreshing_my_listings":
      return "刷新货架中";
    case "my_listings_loaded":
      return "货架已刷新";
    case "retrieving_listing":
      return "取回中";
    case "listing_down":
      return "已下架待取回";
    case "collecting_mail_items":
      return "收取邮件中";
    case "listing_retrieved":
      return "已取回";
    case "panel_opened":
      return "原生面板已打开";
    case "online_recycle_requested":
      return "在线回收已触发";
    case "error":
      return "失败";
    default:
      return formatModuleRuntimeStatus(status);
  }
}

function dailyGiftActionToastLabel(action: DailyGiftRuntimeAction) {
  switch (action) {
    case "claim_daily_gift":
      return "每日礼包领取已发起";
    default:
      return "每日礼包状态已返回";
  }
}

function worldTreeActionToastLabel(action: WorldTreeRuntimeAction) {
  switch (action) {
    case "run_world_tree":
      return "世界树流程已发起";
    default:
      return "世界树状态已返回";
  }
}

function formatDailyGiftRuntimeStatus(status: string) {
  switch (status) {
    case "ready":
      return "就绪";
    case "claiming":
      return "领取中";
    case "claim_succeeded":
      return "领取成功";
    case "already_claimed":
      return "今日已领取";
    case "refreshing_mail":
      return "刷新邮件中";
    case "claimed_mail_collect_requested":
      return "收取附件中";
    case "claimed_mail_collected":
      return "已领取并收取附件";
    case "claimed_mail_not_found":
      return "已领取，未找到邮件";
    case "mail_collect_failed":
      return "邮件收取失败";
    case "claim_failed":
      return "领取失败";
    case "daily_completed":
      return "今日已完成";
    default:
      return formatModuleRuntimeStatus(status);
  }
}

function formatWorldTreeRuntimeStatus(status: string) {
  switch (status) {
    case "running":
      return "执行中";
    case "completed":
      return "已完成";
    case "daily_completed":
      return "今日已完成";
    case "interrupted":
      return "被回城保护中断，等待重试";
    case "failed":
      return "未执行";
    case "stopped":
      return "空闲";
    case "error":
      return "失败";
    default:
      return formatModuleRuntimeStatus(status);
  }
}

function formatDailyGiftMailStatus(status: string) {
  switch (status) {
    case "requested":
      return "已请求收取";
    case "collected":
      return "已收取";
    case "not_found":
      return "未找到待收取邮件";
    default:
      return formatModuleRuntimeStatus(status);
  }
}

function isTradingBankBusyStatus(status: string | undefined) {
  switch (status) {
    case "sending_code":
    case "verifying_code":
    case "validating_target_role":
    case "capturing":
    case "uploading_images":
    case "checking_item":
    case "submitting":
    case "batch_prepared":
    case "batch_next":
    case "refreshing_my_listings":
    case "retrieving_listing":
    case "listing_down":
    case "collecting_mail_items":
      return true;
    default:
      return false;
  }
}

function isDailyGiftBusyStatus(status: string | undefined) {
  switch (status) {
    case "claiming":
    case "claim_succeeded":
    case "already_claimed":
    case "refreshing_mail":
    case "claimed_mail_collect_requested":
      return true;
    default:
      return false;
  }
}

function parseRuntimeJSON(text: string | undefined): Record<string, unknown> {
  if (!text || !text.trim()) return {};
  try {
    const parsed = JSON.parse(text) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringValue(value: unknown): string {
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

function numberValue(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function integerValue(value: unknown): number | null {
  const parsed = numberValue(value);
  return parsed === null ? null : Math.round(parsed);
}

function numberOrString(value: unknown): string {
  return typeof value === "number" || typeof value === "string" ? String(value) : "";
}

function isTradingBankItem(value: unknown): value is TradingBankItemSummary {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function isTradingBankListing(value: unknown): value is TradingBankListingSummary {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as TradingBankListingSummary;
  return record.commodity_id !== undefined && record.commodity_id !== null;
}

function isDailyGiftClaimResult(value: unknown): value is DailyGiftClaimResult {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function isDailyGiftMailCollect(value: unknown): value is DailyGiftMailCollect {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function compareTradingBankItemsByName(
  left: TradingBankItemSummary,
  right: TradingBankItemSummary,
) {
  const nameOrder = String(left.name || "").localeCompare(
    String(right.name || ""),
    "zh-Hans-CN",
    { numeric: true, sensitivity: "base" },
  );
  if (nameOrder !== 0) return nameOrder;
  const idOrder = String(left.item_id ?? "").localeCompare(
    String(right.item_id ?? ""),
    undefined,
    { numeric: true, sensitivity: "base" },
  );
  if (idOrder !== 0) return idOrder;
  return String(left.make_index ?? "").localeCompare(
    String(right.make_index ?? ""),
    undefined,
    { numeric: true, sensitivity: "base" },
  );
}

function compareTradingBankListingsByName(
  left: TradingBankListingSummary,
  right: TradingBankListingSummary,
) {
  const nameOrder = String(left.name || "").localeCompare(
    String(right.name || ""),
    "zh-Hans-CN",
    { numeric: true, sensitivity: "base" },
  );
  if (nameOrder !== 0) return nameOrder;
  const idOrder = String(left.equip_id ?? "").localeCompare(
    String(right.equip_id ?? ""),
    undefined,
    { numeric: true, sensitivity: "base" },
  );
  if (idOrder !== 0) return idOrder;
  return String(left.commodity_id ?? "").localeCompare(
    String(right.commodity_id ?? ""),
    undefined,
    { numeric: true, sensitivity: "base" },
  );
}

function formatTradingBankListingStatus(listing: TradingBankListingSummary) {
  const statusText = typeof listing.status_ch === "string" ? listing.status_ch.trim() : "";
  if (statusText) return statusText;
  const status = Number(listing.status);
  switch (status) {
    case 1:
      return "已上架";
    case 2:
      return "已下架";
    case 3:
      return "出售中";
    case -2:
      return "已失效";
    default:
      return Number.isFinite(status) ? `状态 ${status}` : "未知";
  }
}

function tradingBankListingStatusVariant(status: number | string | undefined) {
  const value = Number(status);
  if (value === 1) return "ok";
  if (value === 2 || value === -2) return "warn";
  if (value === 3) return "neutral";
  return "neutral";
}

function formatTradingBankItemCount(value: number | string | undefined) {
  if (value === undefined || value === null || value === "") return "—";
  const count = Number(value);
  if (Number.isFinite(count) && count > 0) return String(count);
  return String(value);
}

function parseTradingBankBatch(value: unknown): TradingBankBatchStatus | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const index = typeof record.index === "number" ? record.index : Number(record.index);
  const total = typeof record.total === "number" ? record.total : Number(record.total);
  if (!Number.isFinite(index) || !Number.isFinite(total) || total <= 0) {
    return null;
  }
  const results = Array.isArray(record.results)
    ? record.results.flatMap((entry) => {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
        const result = entry as Record<string, unknown>;
        const item = isTradingBankItem(result.item) ? result.item : null;
        const makeIndex = item?.make_index == null ? "" : String(item.make_index);
        if (!makeIndex) return [];
        return [
          {
            ok: result.ok === true,
            make_index: makeIndex,
            error: typeof result.error === "string" ? result.error : "",
          },
        ];
      })
    : [];
  return { index, total, results };
}

function mergeTradingBankRuntime(
  current: InstanceDetailResponse | undefined,
  config: InstanceModuleConfigEntry,
): InstanceDetailResponse | undefined {
  if (!current) return current;
  return {
    ...current,
    module_configs: {
      ...(current.module_configs ?? {}),
      trading_bank_assistant: config,
    },
  };
}

function mergeDailyGiftRuntime(
  current: InstanceDetailResponse | undefined,
  config: InstanceModuleConfigEntry,
): InstanceDetailResponse | undefined {
  if (!current) return current;
  return {
    ...current,
    module_configs: {
      ...(current.module_configs ?? {}),
      box996_daily_gift: config,
    },
  };
}

function mergeWorldTreeRuntime(
  current: InstanceDetailResponse | undefined,
  config: InstanceModuleConfigEntry,
): InstanceDetailResponse | undefined {
  if (!current) return current;
  return {
    ...current,
    module_configs: {
      ...(current.module_configs ?? {}),
      auto_world_tree: config,
    },
  };
}

function useInstanceModuleRuntimeCommand({
  instanceId,
  module,
  label,
}: {
  instanceId: string;
  module: string;
  label: string;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: ModuleRuntimeAction) =>
      apiPostForm<InstanceModuleEditResponse>(
        instanceModuleEditPath(instanceId, module),
        { action },
      ),
    onSuccess: (_response, action) => {
      const actionLabel =
        action === "start" ? "启动" : action === "stop" ? "停止" : "查询";
      toast.success(`${label}${actionLabel}命令已返回`);
      void queryClient.invalidateQueries({ queryKey: ["instance", instanceId] });
    },
    onError: (err) => {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error(`${label}运行控制失败`, { description: msg });
    },
  });
}

function ModuleRuntimeControls({
  online,
  canWrite,
  status,
  lastError,
  pending,
  onAction,
}: {
  online: boolean;
  canWrite: boolean;
  status?: string;
  lastError?: string;
  pending: boolean;
  onAction: (action: ModuleRuntimeAction) => void;
}) {
  const normalized = (status || "").trim();
  const running = normalized === "running";
  const stopped = normalized === "stopped";
  const variant = running ? "ok" : lastError ? "danger" : stopped ? "neutral" : "warn";
  const label = formatModuleRuntimeStatus(normalized) || "未查询";
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded border border-[hsl(var(--border))] p-3">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">运行状态</span>
          <StatusBadge variant={variant}>{label}</StatusBadge>
        </div>
        {lastError ? (
          <span className="break-all text-xs text-[hsl(var(--danger))]">{lastError}</span>
        ) : null}
      </div>
      {canWrite ? (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={pending || !online}
            onClick={() => onAction("query_status")}
            title="查询当前实例状态"
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            查询
          </Button>
          <Button
            size="sm"
            disabled={pending || !online || running}
            onClick={() => onAction("start")}
            title="启动当前实例"
          >
            <Play className="mr-1 h-3.5 w-3.5" />
            启动
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={pending || !online || stopped}
            onClick={() => onAction("stop")}
            title="停止当前实例"
          >
            <Square className="mr-1 h-3.5 w-3.5" />
            停止
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function ModuleActionButtons({
  online,
  hasOverride,
  pending,
  onAction,
}: {
  online: boolean;
  hasOverride: boolean;
  pending: boolean;
  onAction: (action: ModuleAction, desired: string) => void;
}) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      {hasOverride ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={pending}
          onClick={() => onAction(online ? "clear_and_apply" : "clear", "{}")}
        >
          清除实例覆盖
        </Button>
      ) : null}
      <Button
        type="submit"
        size="sm"
        variant="outline"
        disabled={pending}
        data-action="save"
      >
        保存实例覆盖
      </Button>
      <Button
        type="submit"
        size="sm"
        disabled={pending || !online}
        data-action="save_and_apply"
      >
        保存并下发当前实例
      </Button>
    </div>
  );
}

function moduleActionFromSubmit(action: string | undefined): ModuleAction {
  return action === "save_and_apply" ? "save_and_apply" : "save";
}

function ModuleLayerBadges({
  config,
}: {
  config: InstanceModuleConfigEntry | undefined;
}) {
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      {config?.layers?.length ? (
        config.layers.map((layer) => (
          <StatusBadge
            key={`${layer.scope}:${layer.scope_key}:${layer.module}`}
            variant={layer.scope === "instance" ? "ok" : "neutral"}
          >
            {formatModuleScope(layer.scope)}
          </StatusBadge>
        ))
      ) : (
        <StatusBadge variant="neutral">无覆盖</StatusBadge>
      )}
      {config?.instance_override ? (
        <span className="text-[hsl(var(--muted-foreground))]">
          当前实例已覆盖账号默认
        </span>
      ) : (
        <span className="text-[hsl(var(--muted-foreground))]">
          当前使用机器/游戏/账号层合并结果
        </span>
      )}
    </div>
  );
}

type ProfitWindowMode = "60m" | "24h" | "today" | "session";

function InstanceProfitSummaryCard({
  profit,
}: {
  profit?: ProfitSummary | InstanceProfitWindow | null;
}) {
  const summary = profitSummaryValues(profit);
  const hasEvents =
    summary.currencyCount > 0 ||
    summary.materialCount > 0 ||
    summary.activeSeconds > 0 ||
    !!summary.updatedAt;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>最近60分钟收益</CardTitle>
          <StatusBadge variant={summary.sampleStatus === "ok" ? "ok" : "warn"}>
            {summary.sampleStatus === "ok" ? "样本有效" : "样本不足"}
          </StatusBadge>
        </div>
      </CardHeader>
      <CardContent>
        {hasEvents ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            <ProfitMetric label="active" value={formatDurationSec(summary.activeSeconds)} />
            <ProfitMetric label="净值/h" value={formatProfitValueRate(summary.netValuePerHour)} />
            <ProfitMetric label="主要货币/h" value={summary.currencyText} />
            <ProfitMetric label="主要材料/h" value={summary.materialText} />
            <ProfitMetric
              label="更新时间"
              value={summary.updatedAt ? formatMs(summary.updatedAt) : "—"}
            />
          </div>
        ) : (
          <div className="text-sm text-[hsl(var(--muted-foreground))]">
            暂无收益事件。AutoMapHunt 有效运行并上报 profit_events 后会自动刷新。
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProfitMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded border border-[hsl(var(--border))] px-3 py-2">
      <div className="text-xs text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className="mt-1 truncate font-mono text-sm font-medium" title={value}>
        {value || "—"}
      </div>
    </div>
  );
}

function profitSummaryValues(profit?: ProfitSummary | InstanceProfitWindow | null) {
  if (!profit) {
    return {
      activeSeconds: 0,
      sampleStatus: "insufficient",
      netValuePerHour: undefined,
      updatedAt: 0,
      currencyCount: 0,
      materialCount: 0,
      currencyText: "—",
      materialText: "—",
    };
  }
  const total = "totals" in profit ? profit.totals : undefined;
  const currencies = "totals" in profit ? total?.currencies ?? [] : profit.currencies ?? [];
  const materials = "totals" in profit ? total?.materials ?? [] : profit.materials ?? [];
  const primaryCurrency = currencies[0];
  const primaryMaterial = materials[0];
  const netValuePerHour =
    "totals" in profit ? total?.net_value_per_hour : profit.net_value_per_hour;
  return {
    activeSeconds: profit.active_seconds ?? 0,
    sampleStatus: profit.sample_status,
    netValuePerHour,
    updatedAt: profit.updated_at ?? 0,
    currencyCount: currencies.length,
    materialCount: materials.length,
    currencyText: primaryCurrency
      ? `${primaryCurrency.name || `#${primaryCurrency.id}`} ${compactNumber(primaryCurrency.gain_per_hour ?? primaryCurrency.net_per_hour ?? 0)}/h`
      : "—",
    materialText: primaryMaterial
      ? `${primaryMaterial.name || primaryMaterial.key} ×${compactNumber(primaryMaterial.gain_per_hour || 0)}/h`
      : "—",
  };
}

function formatProfitValueRate(value: number | undefined | null): string {
  if (value === undefined || value === null || Number.isNaN(value)) return "—";
  return `${compactNumber(value)}/h`;
}

interface InstanceProfitCardProps {
  instanceId: string;
  detail: InstanceDetailResponse;
}

function InstanceProfitCard({ instanceId, detail }: InstanceProfitCardProps) {
  const [mode, setMode] = useState<ProfitWindowMode>("60m");
  const [includeExcluded, setIncludeExcluded] = useState(false);
  const profitQ = useQuery({
    queryKey: ["instance-profit", instanceId, mode, includeExcluded],
    queryFn: () =>
      apiGet<InstanceProfitResponse>(
        instanceProfitPath(instanceId, mode, "map", includeExcluded),
      ),
    refetchInterval: 30_000,
    initialData:
      mode === "60m" && !includeExcluded
        ? ({ ok: true, ...detail.profit } as InstanceProfitResponse)
        : undefined,
  });
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>收益</CardTitle>
            <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
              {profitWindowLabel(mode)}
              {includeExcluded ? " · 审计视图包含被来源规则排除的流水" : " · 按来源规则计入"}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-md border border-[hsl(var(--border))] p-0.5 text-xs">
              {(["60m", "24h", "today", "session"] as ProfitWindowMode[]).map((item) => (
                <button
                  key={item}
                  type="button"
                  className={`rounded px-2 py-1 ${mode === item ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "text-[hsl(var(--muted-foreground))]"}`}
                  onClick={() => setMode(item)}
                >
                  {profitWindowLabel(item)}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-xs">
              <Checkbox
                checked={includeExcluded}
                onCheckedChange={(value) => setIncludeExcluded(Boolean(value))}
              />
              审计视图
            </label>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <QueryView query={profitQ}>
          {(profit) => <InstanceProfitContent profit={profit} includeExcluded={includeExcluded} />}
        </QueryView>
      </CardContent>
    </Card>
  );
}

function InstanceProfitContent({
  profit,
  includeExcluded,
}: {
  profit: InstanceProfitResponse;
  includeExcluded: boolean;
}) {
  const hasEvents =
    profit.totals.event_count > 0 ||
    profit.totals.currencies.length > 0 ||
    profit.totals.materials.length > 0 ||
    profit.active_seconds > 0;
  if (!hasEvents) {
    return (
      <div className="text-sm text-[hsl(var(--muted-foreground))]">
        暂无收益事件。AutoMapHunt 有效运行并上报 profit_events 后会自动刷新。
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
        <ProfitMetric label="active" value={formatDurationSec(profit.active_seconds)} />
        <ProfitMetric label="wall" value={formatDurationSec(profit.wall_seconds)} />
        <ProfitMetric
          label="毛收益"
          value={formatProfitValue(profit.totals.gross_value, profit.value_currency)}
        />
        <ProfitMetric
          label="成本"
          value={formatProfitValue(profit.totals.cost_value, profit.value_currency)}
        />
        <ProfitMetric
          label="净收益"
          value={formatProfitValue(profit.totals.net_value, profit.value_currency)}
        />
        <ProfitMetric label="净值/h" value={formatProfitValueRate(profit.totals.net_value_per_hour)} />
      </div>

      {includeExcluded ? (
        <StatusBadge variant="warn">审计视图：被来源规则排除的收益/成本也会显示</StatusBadge>
      ) : null}

      {profit.anomalies?.length ? (
        <div className="flex flex-wrap gap-2">
          {profit.anomalies.map((item, idx) => (
            <StatusBadge key={`${item.type}-${item.map_id ?? ""}-${idx}`} variant="warn">
              {item.map_name || item.map_id
                ? `${item.map_name || item.map_id}：${item.message}`
                : item.message}
            </StatusBadge>
          ))}
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <Table dense>
          <TableHeader>
            <TableRow>
              <TableHead>地图</TableHead>
              <TableHead>active</TableHead>
              <TableHead>事件</TableHead>
              <TableHead>毛收益</TableHead>
              <TableHead>成本</TableHead>
              <TableHead>净值/h</TableHead>
              <TableHead>货币/h</TableHead>
              <TableHead>材料/h</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(profit.maps ?? []).map((item) => (
              <TableRow key={item.map_id || "unknown"}>
                <TableCell>
                  {item.map_name || item.map_id || "未识别地图"}{" "}
                  <code className="text-[hsl(var(--muted-foreground))]">
                    #{item.map_id || "unknown"}
                  </code>
                </TableCell>
                <TableCell className="font-mono">{formatDurationSec(item.active_seconds)}</TableCell>
                <TableCell className="font-mono">{compactNumber(item.event_count)}</TableCell>
                <TableCell className="font-mono">
                  {formatProfitValue(item.gross_value, item.value_currency)}
                </TableCell>
                <TableCell className="font-mono">
                  {formatProfitValue(item.cost_value, item.value_currency)}
                </TableCell>
                <TableCell className="font-mono">
                  {formatProfitValueRate(item.net_value_per_hour)}
                </TableCell>
                <TableCell>
                  <ProfitCurrencyRateSummary items={item.currencies} />
                </TableCell>
                <TableCell>
                  <ProfitMaterialRateSummary items={item.materials} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <ProfitCurrencyDetailTable items={profit.totals.currencies} />
        <ProfitMaterialDetailTable items={profit.totals.materials} />
      </div>

      <ProfitRecentEventsTable items={profit.recent_events ?? []} />
    </div>
  );
}

interface MapStatsCardProps {
  detail: InstanceDetailResponse;
}

function MapStatsCard({ detail }: MapStatsCardProps) {
  const rows = [...detail.map_stats].sort((a, b) => (b.dwell_sec ?? 0) - (a.dwell_sec ?? 0));
  if (rows.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>地图统计</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            该实例还没有累计地图统计。AutoMapHunt 跑出数据后会自动上报。
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          地图统计{" "}
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
            累计收益来自 AutoMapHunt lifetime
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table dense>
          <TableHeader>
            <TableRow>
              <TableHead>地图</TableHead>
              <TableHead>累计击杀</TableHead>
              <TableHead>累计停留</TableHead>
              <TableHead>累计收益</TableHead>
              <TableHead>kills/min</TableHead>
              <TableHead>材料</TableHead>
              <TableHead>刷怪间隔</TableHead>
              <TableHead>进入/空场/逃跑/争夺</TableHead>
              <TableHead>更新时间</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((item) => (
              <TableRow key={item.map_id}>
                <TableCell>
                  {item.map_name || "—"}{" "}
                  <code className="text-[hsl(var(--muted-foreground))]">
                    #{item.map_id}
                  </code>
                </TableCell>
                <TableCell className="font-mono">{compactNumber(item.kills)}</TableCell>
                <TableCell className="font-mono">{formatDurationSec(item.dwell_sec)}</TableCell>
                <TableCell>
                  <MapCurrencySummary items={item.currencies} />
                </TableCell>
                <TableCell className="font-mono">{killsPerMin(item.kills, item.dwell_sec)}</TableCell>
                <TableCell>
                  <MapMaterialSummary items={item.materials} />
                </TableCell>
                <TableCell className="font-mono">
                  {item.respawn_avg !== undefined && item.respawn_avg !== null
                    ? `${item.respawn_avg.toFixed(1)}s`
                    : "—"}
                </TableCell>
                <TableCell className="font-mono text-[10px]">
                  {`${item.enter_count} / ${item.empty_count} / ${item.flee_count} / ${item.contested_count}`}
                </TableCell>
                <TableCell className="text-[hsl(var(--muted-foreground))]">
                  {item.updated_at ? formatMs(item.updated_at) : ""}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function profitWindowLabel(value: ProfitWindowMode): string {
  if (value === "60m") return "最近60分钟";
  if (value === "24h") return "最近24小时";
  if (value === "today") return "今日";
  return "本会话";
}

function formatProfitValue(
  value: number | undefined | null,
  currency?: string | null,
): string {
  if (value === undefined || value === null || Number.isNaN(value)) return "—";
  return `${compactNumber(value)}${currency ? ` ${currency}` : ""}`;
}

function ProfitCurrencyDetailTable({ items }: { items: ProfitCurrencyAmount[] }) {
  return (
    <div className="overflow-x-auto">
      <div className="mb-2 text-sm font-medium">货币明细</div>
      {items.length === 0 ? (
        <div className="text-xs text-[hsl(var(--muted-foreground))]">无货币收益</div>
      ) : (
        <Table dense>
          <TableHeader>
            <TableRow>
              <TableHead>货币</TableHead>
              <TableHead>gain</TableHead>
              <TableHead>spend</TableHead>
              <TableHead>net</TableHead>
              <TableHead>net/h</TableHead>
              <TableHead>估值</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  {item.name || `#${item.id}`}{" "}
                  <code className="text-[hsl(var(--muted-foreground))]">#{item.id}</code>
                </TableCell>
                <TableCell className="font-mono">{compactNumber(item.gain)}</TableCell>
                <TableCell className="font-mono">{compactNumber(item.spend)}</TableCell>
                <TableCell className="font-mono">{compactNumber(item.net)}</TableCell>
                <TableCell className="font-mono">{formatProfitValueRate(item.net_per_hour)}</TableCell>
                <TableCell className="text-xs">
                  {item.unit_value
                    ? `${compactNumber(item.unit_value)} ${item.value_currency || ""} · ${item.value_rule_source || "rule"}`
                    : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function ProfitMaterialDetailTable({ items }: { items: ProfitMaterialAmount[] }) {
  return (
    <div className="overflow-x-auto">
      <div className="mb-2 text-sm font-medium">材料明细</div>
      {items.length === 0 ? (
        <div className="text-xs text-[hsl(var(--muted-foreground))]">无材料收益</div>
      ) : (
        <Table dense>
          <TableHeader>
            <TableRow>
              <TableHead>材料</TableHead>
              <TableHead>gain</TableHead>
              <TableHead>gain/h</TableHead>
              <TableHead>估值/h</TableHead>
              <TableHead>置信</TableHead>
              <TableHead>规则</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.key}>
                <TableCell>
                  {item.name || item.key}{" "}
                  <code className="text-[hsl(var(--muted-foreground))]">
                    {item.item_id ? `#${item.item_id}` : item.key}
                  </code>
                </TableCell>
                <TableCell className="font-mono">{compactNumber(item.gain)}</TableCell>
                <TableCell className="font-mono">{formatProfitValueRate(item.gain_per_hour)}</TableCell>
                <TableCell className="font-mono">{formatProfitValueRate(item.value_per_hour)}</TableCell>
                <TableCell>
                  <StatusBadge variant={item.confidence === "name_match" ? "warn" : "neutral"}>
                    {item.confidence === "name_match" ? "名称匹配" : "观测"}
                  </StatusBadge>
                </TableCell>
                <TableCell className="text-xs">
                  {item.unit_value
                    ? `${compactNumber(item.unit_value)} ${item.value_currency || ""} · ${item.value_rule_source || "rule"}`
                    : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function ProfitRecentEventsTable({ items }: { items: ProfitEventRecord[] }) {
  return (
    <div className="overflow-x-auto">
      <div className="mb-2 text-sm font-medium">最近事件</div>
      {items.length === 0 ? (
        <div className="text-xs text-[hsl(var(--muted-foreground))]">无最近事件</div>
      ) : (
        <Table dense>
          <TableHeader>
            <TableRow>
              <TableHead>时间</TableHead>
              <TableHead>来源</TableHead>
              <TableHead>地图</TableHead>
              <TableHead>对象</TableHead>
              <TableHead>gain</TableHead>
              <TableHead>spend</TableHead>
              <TableHead>seq</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="text-[hsl(var(--muted-foreground))]">
                  {formatMs(item.event_at)}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {item.module || "auto_map_hunt"} / {item.source_type}
                </TableCell>
                <TableCell>
                  {item.map_name || item.map_id || "—"}{" "}
                  {item.map_id ? (
                    <code className="text-[hsl(var(--muted-foreground))]">#{item.map_id}</code>
                  ) : null}
                </TableCell>
                <TableCell>
                  {item.item_name || item.currency_id || "—"}
                  {item.item_id ? (
                    <code className="ml-1 text-[hsl(var(--muted-foreground))]">#{item.item_id}</code>
                  ) : null}
                </TableCell>
                <TableCell className="font-mono">{compactNumber(item.gain)}</TableCell>
                <TableCell className="font-mono">{compactNumber(item.spend)}</TableCell>
                <TableCell className="font-mono text-xs">{item.seq}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function MapCurrencySummary({
  items,
}: {
  items: InstanceDetailResponse["map_stats"][number]["currencies"];
}) {
  if (!items || items.length === 0) {
    return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
  }
  return (
    <div className="flex min-w-40 flex-col gap-1">
      {items.map((item) => (
        <span
          key={item.id}
          className="whitespace-nowrap text-xs"
          title={`gain=${item.gain} spend=${item.spend} net=${item.net}`}
        >
          <span>{item.name || `#${item.id}`}</span>{" "}
          <strong className="font-mono">{formatCurrencyAmount(item.gain)}</strong>
          {item.spend > 0 ? (
            <span className="font-mono text-[hsl(var(--muted-foreground))]">
              {" "}
              -{formatCurrencyAmount(item.spend)}
            </span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

function MapMaterialSummary({
  items,
}: {
  items: InstanceDetailResponse["map_stats"][number]["materials"];
}) {
  if (!items || items.length === 0) {
    return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
  }
  return (
    <div className="flex min-w-32 flex-col gap-1">
      {items.map((item) => (
        <span key={item.name} className="whitespace-nowrap text-xs">
          {item.name}{" "}
          <strong className="font-mono">×{compactNumber(item.count)}</strong>
        </span>
      ))}
    </div>
  );
}

function ProfitCurrencyRateSummary({
  items,
}: {
  items: ProfitMapAggregate["currencies"];
}) {
  if (!items || items.length === 0) {
    return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
  }
  return (
    <div className="flex min-w-40 flex-col gap-1">
      {items.slice(0, 3).map((item) => (
        <span key={item.id} className="whitespace-nowrap text-xs">
          <span>{item.name || `#${item.id}`}</span>{" "}
          <strong className="font-mono">
            {compactNumber(item.net_per_hour || item.gain_per_hour || 0)}/h
          </strong>
        </span>
      ))}
    </div>
  );
}

function ProfitMaterialRateSummary({
  items,
}: {
  items: ProfitMapAggregate["materials"];
}) {
  if (!items || items.length === 0) {
    return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
  }
  return (
    <div className="flex min-w-36 flex-col gap-1">
      {items.slice(0, 3).map((item) => (
        <span key={item.key} className="whitespace-nowrap text-xs">
          {item.name || item.key}{" "}
          <strong className="font-mono">
            ×{compactNumber(item.gain_per_hour || 0)}/h
          </strong>
          {item.confidence === "name_match" ? (
            <span className="ml-1 text-[hsl(var(--muted-foreground))]">低置信</span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

function formatCurrencyAmount(value: number | undefined | null): string {
  if (value === undefined || value === null || Number.isNaN(value)) return "0";
  return compactNumber(value);
}

function parseAutoMapHuntDesired(raw: string): {
  enabled: boolean;
  cutUpgradeEnabled: boolean;
  selectAllMaps: boolean;
  selectedMapIds: string[];
} {
  try {
    const parsed = JSON.parse(raw || "{}") as {
      enabled?: boolean;
      cutUpgrade?: { enabled?: boolean };
      selectAllMaps?: boolean;
      selectedMapIds?: unknown[];
    };
    return {
      enabled: parsed.enabled === true,
      cutUpgradeEnabled:
        parsed.cutUpgrade?.enabled === undefined ? true : parsed.cutUpgrade.enabled === true,
      selectAllMaps: parsed.selectAllMaps === true,
      selectedMapIds: Array.isArray(parsed.selectedMapIds)
        ? parsed.selectedMapIds
            .map((v) => String(v).trim())
            .filter((v) => v.length > 0)
        : [],
    };
  } catch {
    return { enabled: false, cutUpgradeEnabled: true, selectAllMaps: false, selectedMapIds: [] };
  }
}

function parseBossChallengeDesired(raw: string): {
  enabled: boolean;
} {
  try {
    const parsed = JSON.parse(raw || "{}") as { enabled?: boolean };
    return { enabled: parsed.enabled === true };
  } catch {
    return { enabled: false };
  }
}

function selectableAutoMapIDs(bundle: GameBundleResponse, continentID?: string) {
  const enabledContinents = new Map(
    bundle.bundle.continents.map((c) => [c.continent_id, c.enabled]),
  );
  return bundle.bundle.maps
    .filter(
      (m) =>
        (continentID === undefined || m.continent_id === continentID) &&
        enabledContinents.get(m.continent_id) !== false &&
        !m.unreachable,
    )
    .map((m) => m.map_id);
}

function addSelectedMapIDs(prev: Set<string>, ids: string[]) {
  const next = new Set(prev);
  for (const id of ids) next.add(id);
  return next;
}

function removeSelectedMapIDs(prev: Set<string>, ids: string[]) {
  const next = new Set(prev);
  for (const id of ids) next.delete(id);
  return next;
}

function invertSelectedMapIDs(
  prev: Set<string>,
  allIDs: string[],
  selectableIDs: string[],
) {
  const next = new Set(prev);
  const selectable = new Set(selectableIDs);
  for (const id of allIDs) {
    if (next.has(id)) {
      next.delete(id);
    } else if (selectable.has(id)) {
      next.add(id);
    }
  }
  return next;
}
